<script lang="ts">
  /**
   * ProjectConfigPanel.svelte - Panel for editing project YAML metadata
   *
   * Allows editing EPUB/Pandoc/AutomaticBook metadata in a form interface.
   * The data is stored as YAML in project.metadata field.
   */

  import { createEventDispatcher } from 'svelte';
  import { selectedProject, selectedPreset, presetMode, presets, appActions, images, templates } from '../../stores/appStore';
  import type { Image, CreateImageInput, CreateTemplateInput, Preset } from '../../types';

  const dispatch = createEventDispatcher<{ close: void }>();

  // Creator role types (primary: author/editor)
  type CreatorRole = 'author' | 'editor';

  // Contributor role types (secondary: translator, illustrator, etc.)
  type ContributorRole = 'translator' | 'illustrator' | 'photographer' | 'writer of foreword' | 'writer of introduction' | 'writer of preface' | 'writer of afterword';

  interface Creator {
    name: string;
    role: CreatorRole;
  }

  interface Contributor {
    name: string;
    role: ContributorRole;
  }

  interface Identifier {
    scheme: string;
    text: string;
  }

  interface Subject {
    text: string;
    authority: string;
    term: string;
  }

  interface ProjectMetadata {
    // Basic info
    title: string;
    subtitle: string;
    originalTitle: string;
    lang: string;
    originalLanguage: string;

    // Creators (primary: author/editor)
    creators: Creator[];
    // Contributors (secondary: translator, illustrator, etc.)
    contributors: Contributor[];
    translatorDisplay: string;
    criticalDisplay: string;

    // Publisher info
    publisher: string;
    rights: string;
    date: string;
    publishedPrint: string;
    place: string;
    serie: string;

    // Identifiers (unified: ISBN digital, ISBN Print, ISSN, DOI, UUID, URL)
    identifiers: Identifier[];

    // EPUB options
    coverImage: string;
    coverNote: string;
    navReorder: boolean;
    toc: boolean;
    tocDepth: number;
    tocTitle: string;

    // Content
    dedication: string;
    description: string;
    funding: string;
    editions: string;
    coverCredit: string;

    // Price
    price: string;

    // Categories with BISAC
    subjects: Subject[];

    // Accessibility (Ace by DAISY)
    accessModes: string[];
    accessModeSufficient: string[];
    accessibilityHazards: string[];
    accessibilityFeatures: string[];
    accessibilitySummary: string;

    // Custom fields
    customFields: { key: string; value: string }[];
  }

  // Default metadata structure with proper accessibility defaults
  const defaultMetadata: ProjectMetadata = {
    title: '',
    subtitle: '',
    originalTitle: '',
    lang: 'fr',
    originalLanguage: '',
    creators: [{ name: '', role: 'author' }],
    contributors: [],
    translatorDisplay: '',
    publisher: '',
    rights: '',
    date: new Date().toISOString().slice(0, 7), // YYYY-MM format
    publishedPrint: '',
    place: '',
    serie: '',
    identifiers: [{ scheme: 'ISBN', text: '' }],
    coverImage: '',
    coverNote: '',
    navReorder: true,
    toc: true,
    tocDepth: 2,
    tocTitle: '',
    criticalDisplay: '',
    dedication: '',
    description: '',
    funding: '',
    editions: '',
    coverCredit: '',
    price: '',
    subjects: [{ text: '', authority: 'BISAC', term: '' }],
    // Default accessibility values per Ace by DAISY
    accessModes: ['textual'],
    accessModeSufficient: ['textual'],
    accessibilityHazards: ['none'],
    accessibilityFeatures: ['alternativeText', 'readingOrder', 'structuralNavigation', 'tableOfContents'],
    accessibilitySummary: 'Ce livre est accessible.',
    customFields: []
  };

  // Current metadata state
  let metadata: ProjectMetadata = { ...defaultMetadata };
  let isSaving = false;
  let hasChanges = false;

  // Available roles for creators (primary responsibility)
  const creatorRoles: { value: CreatorRole; label: string }[] = [
    { value: 'author', label: 'Auteur' },
    { value: 'editor', label: 'Editeur' }
  ];

  // Available roles for contributors (secondary participation)
  const contributorRoles: { value: ContributorRole; label: string }[] = [
    { value: 'translator', label: 'Traducteur' },
    { value: 'illustrator', label: 'Illustrateur' },
    { value: 'photographer', label: 'Photographe' },
    { value: 'writer of foreword', label: 'Preface' },
    { value: 'writer of introduction', label: 'Introduction' },
    { value: 'writer of preface', label: 'Avant-propos' },
    { value: 'writer of afterword', label: 'Postface' }
  ];

  // Available languages (30 languages + custom mode, matching AutomaticBook)
  const languages = [
    { value: 'ar', label: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629 (ar)' },
    { value: 'bn', label: '\u09AC\u09BE\u0982\u09B2\u09BE (bn)' },
    { value: 'de', label: 'Deutsch (de)' },
    { value: 'en', label: 'English (en)' },
    { value: 'es', label: 'Espa\u00f1ol (es)' },
    { value: 'fa', label: '\u0641\u0627\u0631\u0633\u06CC (fa)' },
    { value: 'fr', label: 'Fran\u00e7ais (fr)' },
    { value: 'gu', label: '\u0A97\u0AC1\u0A9C\u0AB0\u0ABE\u0AA4\u0AC0 (gu)' },
    { value: 'hi', label: '\u0939\u093F\u0928\u094D\u0926\u0940 (hi)' },
    { value: 'id', label: 'Bahasa Indonesia (id)' },
    { value: 'it', label: 'Italiano (it)' },
    { value: 'ja', label: '\u65E5\u672C\u8A9E (ja)' },
    { value: 'ko', label: '\uD55C\uAD6D\uC5B4 (ko)' },
    { value: 'mr', label: '\u092E\u0930\u093E\u0920\u0940 (mr)' },
    { value: 'ms', label: 'Bahasa Melayu (ms)' },
    { value: 'pa', label: '\u0A2A\u0A70\u0A1C\u0A3E\u0A2C\u0A40 (pa)' },
    { value: 'pl', label: 'Polski (pl)' },
    { value: 'pt', label: 'Portugu\u00eas (pt)' },
    { value: 'ru', label: '\u0420\u0443\u0441\u0441\u043A\u0438\u0439 (ru)' },
    { value: 'sw', label: 'Kiswahili (sw)' },
    { value: 'ta', label: '\u0BA4\u0BAE\u0BBF\u0BB4\u0BCD (ta)' },
    { value: 'te', label: '\u0C24\u0C46\u0C32\u0C41\u0C17\u0C41 (te)' },
    { value: 'th', label: '\u0E44\u0E17\u0E22 (th)' },
    { value: 'tr', label: 'T\u00fcrk\u00e7e (tr)' },
    { value: 'uk', label: '\u0423\u043A\u0440\u0430\u0457\u043D\u0441\u044C\u043A\u0430 (uk)' },
    { value: 'ur', label: '\u0627\u0631\u062F\u0648 (ur)' },
    { value: 'vi', label: 'Ti\u1EBFng Vi\u1EC7t (vi)' },
    { value: 'zh', label: '\u4E2D\u6587 (zh)' }
  ];
  const langValues = new Set(languages.map(l => l.value));
  let customLang = false;
  let customOrigLang = false;
  $: isCustomLang = customLang || (metadata.lang !== '' && !langValues.has(metadata.lang));
  $: isCustomOrigLang = customOrigLang || (metadata.originalLanguage !== '' && !langValues.has(metadata.originalLanguage));

  // Identifier schemes (unified, matching AutomaticBook)
  const identifierSchemes = [
    { value: 'ISBN', label: 'ISBN numerique' },
    { value: 'ISBN Print', label: 'ISBN imprime' },
    { value: 'ISSN', label: 'ISSN' },
    { value: 'DOI', label: 'DOI' },
    { value: 'UUID', label: 'UUID' },
    { value: 'URL', label: 'URL' }
  ];

  // Subject authorities (matching AutomaticBook — no Custom)
  const subjectAuthorities = ['BISAC', 'BIC', 'CLIL', 'Thema'];

  // BISAC lazy-load
  let bisacCategories: { code: string; desc: string }[] = [];
  let bisacLoaded = false;
  let bisacSearches: Record<number, string> = {};
  let bisacOpenId: number | null = null;

  async function ensureBisacLoaded(): Promise<void> {
    if (bisacLoaded) return;
    bisacLoaded = true;
    const mod = await import('../../utils/bisac');
    bisacCategories = mod.BISAC_CATEGORIES;
  }

  // Reactive filtered results — must be a $: declaration so Svelte 4 tracks
  // bisacCategories, bisacSearches and bisacOpenId as template dependencies.
  // (Function calls in templates hide inner variable accesses from Svelte's compiler.)
  $: activeBisacFiltered = (() => {
    if (bisacOpenId === null) return [];
    const q = (bisacSearches[bisacOpenId] || '').toLowerCase();
    if (!q) return bisacCategories.slice(0, 30);
    return bisacCategories.filter(c =>
      c.desc.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    ).slice(0, 30);
  })();

  function selectBisac(index: number, cat: { code: string; desc: string }): void {
    metadata.subjects[index].text = cat.desc;
    metadata.subjects[index].term = cat.code;
    bisacOpenId = null;
    bisacSearches[index] = '';
    markChanged();
  }

  // Access modes
  const accessModeOptions = ['textual', 'visual', 'auditory'];

  // Accessibility hazards
  const accessibilityHazardOptions = ['none', 'flashing', 'motionSimulation', 'sound', 'noFlashingHazard', 'noMotionSimulationHazard', 'noSoundHazard'];

  // Accessibility features
  const accessibilityFeatureOptions = [
    'alternativeText',
    'readingOrder',
    'structuralNavigation',
    'tableOfContents',
    'longDescription',
    'highContrastDisplay',
    'largePrint',
    'displayTransformability',
    'synchronizedAudioText'
  ];

  // Parse YAML string to metadata object (simplified - handles common patterns)
  function parseYaml(yaml: string): Partial<ProjectMetadata> {
    if (!yaml) return {};

    const result: Partial<ProjectMetadata> = {};
    const lines = yaml.split('\n');
    let i = 0;

    const parseMultiline = (startIdx: number): { value: string; endIdx: number } => {
      let value = '';
      let idx = startIdx;
      while (idx < lines.length) {
        const line = lines[idx];
        if (line.match(/^\s{2,}/) || line.trim() === '') {
          value += (value ? '\n' : '') + line.replace(/^\s{2}/, '');
          idx++;
        } else if (!line.startsWith(' ') && line.includes(':')) {
          break;
        } else {
          idx++;
        }
      }
      // Convert <br> tags to newlines (AutomaticBook compatibility)
      return { value: value.trim().replace(/<br\s*\/?>/gi, '\n'), endIdx: idx - 1 };
    };

    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();

      if (!trimmed || trimmed.startsWith('#') || trimmed === '---') {
        i++;
        continue;
      }

      const colonIdx = trimmed.indexOf(':');
      if (colonIdx > 0) {
        const key = trimmed.slice(0, colonIdx).trim();
        const value = trimmed.slice(colonIdx + 1).trim();

        switch (key) {
          case 'lang':
            result.lang = value.replace(/"/g, '');
            break;
          case 'original-language':
            result.originalLanguage = value.replace(/"/g, '');
            break;
          case 'publisher':
            result.publisher = value.replace(/"/g, '');
            break;
          case 'date':
            result.date = value.replace(/"/g, '');
            break;
          case 'original-title':
            result.originalTitle = value.replace(/"/g, '');
            break;
          case 'published-print':
            result.publishedPrint = value.replace(/"/g, '');
            break;
          case 'isbn-print': {
            // Unify isbn-print into identifiers array as "ISBN Print"
            const isbnPrintVal = value.replace(/"/g, '');
            if (isbnPrintVal) {
              if (!result.identifiers) result.identifiers = [];
              result.identifiers.push({ scheme: 'ISBN Print', text: isbnPrintVal });
            }
            break;
          }
          case 'price':
            result.price = value.replace(/"/g, '');
            break;
          case 'place':
            result.place = value.replace(/"/g, '');
            break;
          case 'serie':
            result.serie = value.replace(/"/g, '');
            break;
          case 'funding':
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              result.funding = multiVal;
              i = endIdx;
            } else {
              result.funding = value.replace(/"/g, '').replace(/<br\s*\/?>/gi, '\n');
            }
            break;
          case 'editions':
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              result.editions = multiVal;
              i = endIdx;
            } else {
              result.editions = value.replace(/"/g, '').replace(/<br\s*\/?>/gi, '\n');
            }
            break;
          case 'critical-display':
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              result.criticalDisplay = multiVal;
              i = endIdx;
            } else {
              result.criticalDisplay = value.replace(/"/g, '').replace(/<br\s*\/?>/gi, '\n');
            }
            break;
          case 'cover-image':
            result.coverImage = value.replace(/"/g, '');
            break;
          case 'rights':
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              result.rights = multiVal;
              i = endIdx;
            } else {
              result.rights = value.replace(/"/g, '').replace(/<br\s*\/?>/gi, '\n');
            }
            break;
          case 'dedication':
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              result.dedication = multiVal;
              i = endIdx;
            } else {
              result.dedication = value.replace(/"/g, '').replace(/<br\s*\/?>/gi, '\n');
            }
            break;
          case 'description':
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              result.description = multiVal;
              i = endIdx;
            } else {
              result.description = value.replace(/"/g, '');
            }
            break;
          case 'translator-display':
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              result.translatorDisplay = multiVal;
              i = endIdx;
            } else {
              result.translatorDisplay = value.replace(/"/g, '').replace(/<br\s*\/?>/gi, '\n');
            }
            break;
          case 'cover-note':
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              result.coverNote = multiVal;
              i = endIdx;
            } else {
              result.coverNote = value.replace(/"/g, '');
            }
            break;
          case 'cover-credit':
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              result.coverCredit = multiVal;
              i = endIdx;
            } else {
              result.coverCredit = value.replace(/"/g, '').replace(/<br\s*\/?>/gi, '\n');
            }
            break;
          case 'accessibilitySummary':
            if (value === '|' || value === '>') {
              const { value: multiVal, endIdx } = parseMultiline(i + 1);
              result.accessibilitySummary = multiVal;
              i = endIdx;
            } else {
              result.accessibilitySummary = value.replace(/"/g, '');
            }
            break;
          case 'title':
            // Parse title block
            if (!value) {
              i++;
              while (i < lines.length && lines[i].startsWith('  ')) {
                const tline = lines[i].trim();
                if (tline.startsWith('- type: main')) {
                  i++;
                  if (i < lines.length && lines[i].includes('text:')) {
                    result.title = lines[i].split('text:')[1].trim().replace(/"/g, '');
                  }
                } else if (tline.startsWith('- type: subtitle')) {
                  i++;
                  if (i < lines.length && lines[i].includes('text:')) {
                    result.subtitle = lines[i].split('text:')[1].trim().replace(/"/g, '');
                  }
                }
                i++;
              }
              i--;
            } else {
              result.title = value.replace(/"/g, '');
            }
            break;
          case 'creator': {
            // Parse creators
            result.creators = [];
            i++;
            let currentCreator: Partial<Creator> = {};
            while (i < lines.length && lines[i].startsWith('  ')) {
              const cline = lines[i].trim();
              if (cline.startsWith('- role:')) {
                if (currentCreator.name) {
                  result.creators.push(currentCreator as Creator);
                }
                currentCreator = { role: cline.split(':')[1].trim() as CreatorRole };
              } else if (cline.startsWith('text:')) {
                currentCreator.name = cline.split(':').slice(1).join(':').trim().replace(/"/g, '');
              }
              i++;
            }
            if (currentCreator.name) {
              result.creators.push(currentCreator as Creator);
            }
            i--;
            break;
          }
          case 'contributor': {
            // Parse contributors (secondary roles)
            result.contributors = [];
            i++;
            let currentContributor: Partial<Contributor> = {};
            while (i < lines.length && lines[i].startsWith('  ')) {
              const cline = lines[i].trim();
              if (cline.startsWith('- role:')) {
                if (currentContributor.name) {
                  result.contributors.push(currentContributor as Contributor);
                }
                currentContributor = { role: cline.split(':')[1].trim() as ContributorRole };
              } else if (cline.startsWith('text:')) {
                currentContributor.name = cline.split(':').slice(1).join(':').trim().replace(/"/g, '');
              }
              i++;
            }
            if (currentContributor.name) {
              result.contributors.push(currentContributor as Contributor);
            }
            i--;
            break;
          }
          case 'identifier': {
            // Parse identifiers
            result.identifiers = [];
            i++;
            let currentId: Partial<Identifier> = {};
            while (i < lines.length && lines[i].startsWith('  ')) {
              const idline = lines[i].trim();
              if (idline.startsWith('- scheme:')) {
                if (currentId.text) {
                  result.identifiers.push(currentId as Identifier);
                }
                currentId = { scheme: idline.split(':')[1].trim().replace(/"/g, '') };
              } else if (idline.startsWith('text:')) {
                currentId.text = idline.split(':').slice(1).join(':').trim().replace(/"/g, '');
              }
              i++;
            }
            if (currentId.text) {
              result.identifiers.push(currentId as Identifier);
            }
            i--;
            break;
          }
          case 'subject': {
            // Parse subjects with BISAC
            result.subjects = [];
            i++;
            let currentSubject: Partial<Subject> = {};
            while (i < lines.length && lines[i].startsWith('  ')) {
              const sline = lines[i].trim();
              if (sline.startsWith('- text:')) {
                if (currentSubject.text) {
                  result.subjects.push(currentSubject as Subject);
                }
                currentSubject = { text: sline.split(':').slice(1).join(':').trim().replace(/"/g, ''), authority: '', term: '' };
              } else if (sline.startsWith('authority:')) {
                currentSubject.authority = sline.split(':')[1].trim();
              } else if (sline.startsWith('term:')) {
                currentSubject.term = sline.split(':')[1].trim().replace(/"/g, '');
              }
              i++;
            }
            if (currentSubject.text) {
              result.subjects.push(currentSubject as Subject);
            }
            i--;
            break;
          }
          case 'accessModes':
            result.accessModes = [];
            i++;
            while (i < lines.length && lines[i].trim().startsWith('-')) {
              const mode = lines[i].trim().slice(1).trim();
              if (mode) result.accessModes.push(mode);
              i++;
            }
            i--;
            break;
          case 'accessModeSufficient':
            result.accessModeSufficient = [];
            i++;
            while (i < lines.length && lines[i].trim().startsWith('-')) {
              const mode = lines[i].trim().slice(1).trim();
              if (mode) result.accessModeSufficient.push(mode);
              i++;
            }
            i--;
            break;
          case 'accessibilityHazards':
            result.accessibilityHazards = [];
            i++;
            while (i < lines.length && lines[i].trim().startsWith('-')) {
              const hazard = lines[i].trim().slice(1).trim();
              if (hazard) result.accessibilityHazards.push(hazard);
              i++;
            }
            i--;
            break;
          case 'accessibilityFeatures':
            result.accessibilityFeatures = [];
            i++;
            while (i < lines.length && lines[i].trim().startsWith('-')) {
              const feature = lines[i].trim().slice(1).trim();
              if (feature) result.accessibilityFeatures.push(feature);
              i++;
            }
            i--;
            break;
          default: {
            // Collect unknown fields as custom fields
            if (key && key !== '---') {
              if (!result.customFields) result.customFields = [];
              let fieldValue = '';
              if (value === '|' || value === '>') {
                const { value: multiVal, endIdx } = parseMultiline(i + 1);
                fieldValue = multiVal;
                i = endIdx;
              } else {
                fieldValue = value.replace(/"/g, '');
              }
              if (fieldValue) {
                result.customFields.push({ key, value: fieldValue });
              }
            }
            break;
          }
        }
      }
      i++;
    }

    return result;
  }

  // Convert metadata object to YAML string
  function toYaml(meta: ProjectMetadata): string {
    const lines: string[] = ['---'];

    // Title block
    if (meta.title) {
      lines.push('title:');
      lines.push('  - type: main');
      lines.push(`    text: "${meta.title}"`);
      if (meta.subtitle) {
        lines.push('  - type: subtitle');
        lines.push(`    text: "${meta.subtitle}"`);
      }
    }

    // Creators (primary: author/editor)
    if (meta.creators.length > 0 && meta.creators.some(c => c.name)) {
      lines.push('');
      lines.push('creator:');
      for (const creator of meta.creators) {
        if (creator.name) {
          lines.push(`  - role: ${creator.role}`);
          lines.push(`    text: "${creator.name}"`);
        }
      }
    }

    // Contributors (secondary roles)
    if (meta.contributors && meta.contributors.length > 0 && meta.contributors.some(c => c.name)) {
      lines.push('');
      lines.push('contributor:');
      for (const contributor of meta.contributors) {
        if (contributor.name) {
          lines.push(`  - role: ${contributor.role}`);
          lines.push(`    text: "${contributor.name}"`);
        }
      }
    }

    // Basic fields
    lines.push('');
    if (meta.lang) lines.push(`lang: ${meta.lang}`);
    if (meta.originalLanguage) lines.push(`original-language: ${meta.originalLanguage}`);
    if (meta.publisher) lines.push(`publisher: "${meta.publisher}"`);

    // Rights (multiline)
    if (meta.rights) {
      lines.push('rights: |');
      for (const rline of meta.rights.split('\n')) {
        lines.push(`  ${rline}`);
      }
    }

    if (meta.date) lines.push(`date: "${meta.date}"`);

    // Identifiers — ISBN Print stored separately as top-level isbn-print
    const nonPrintIds = meta.identifiers.filter(id => id.text && id.scheme !== 'ISBN Print');
    const printIds = meta.identifiers.filter(id => id.text && id.scheme === 'ISBN Print');

    if (nonPrintIds.length > 0) {
      lines.push('');
      lines.push('identifier:');
      for (const id of nonPrintIds) {
        lines.push(`  - scheme: ${id.scheme}`);
        lines.push(`    text: "${id.text}"`);
      }
    }

    // isbn-print as top-level key (Pandoc compatibility)
    if (printIds.length > 0) {
      lines.push(`isbn-print: "${printIds[0].text}"`);
    }

    // Cover
    if (meta.coverImage) {
      lines.push('');
      lines.push(`cover-image: "${meta.coverImage}"`);
    }

    // Dedication
    if (meta.dedication) {
      lines.push('');
      lines.push('dedication: |');
      for (const dline of meta.dedication.split('\n')) {
        lines.push(`  ${dline}`);
      }
    }

    // Description
    if (meta.description) {
      lines.push('');
      lines.push('description: |');
      for (const dline of meta.description.split('\n')) {
        lines.push(`  ${dline}`);
      }
    }

    // Subjects with BISAC
    if (meta.subjects.length > 0 && meta.subjects.some(s => s.text)) {
      lines.push('');
      lines.push('subject:');
      for (const subject of meta.subjects) {
        if (subject.text) {
          lines.push(`    - text: "${subject.text}"`);
          if (subject.authority) lines.push(`      authority: ${subject.authority}`);
          if (subject.term) lines.push(`      term: "${subject.term}"`);
        }
      }
    }

    // Accessibility section
    lines.push('');
    lines.push('# --- Accessibility (Ace by DAISY) ---');

    if (meta.accessModes.length > 0) {
      lines.push('accessModes:');
      for (const mode of meta.accessModes) {
        lines.push(`  - ${mode}`);
      }
    }

    if (meta.accessModeSufficient.length > 0) {
      lines.push('');
      lines.push('accessModeSufficient:');
      for (const mode of meta.accessModeSufficient) {
        lines.push(`  - ${mode}`);
      }
    }

    if (meta.accessibilityHazards.length > 0) {
      lines.push('');
      lines.push('accessibilityHazards:');
      for (const hazard of meta.accessibilityHazards) {
        lines.push(`  - ${hazard}`);
      }
    }

    if (meta.accessibilityFeatures.length > 0) {
      lines.push('');
      lines.push('accessibilityFeatures:');
      for (const feature of meta.accessibilityFeatures) {
        lines.push(`  - ${feature}`);
      }
    }

    if (meta.accessibilitySummary) {
      lines.push('');
      lines.push('accessibilitySummary: >');
      lines.push(`  ${meta.accessibilitySummary}`);
    }

    // AutomaticBook fields
    lines.push('');
    lines.push('# --- Additional fields for AutomaticBook ---');

    if (meta.translatorDisplay) {
      lines.push('translator-display: |');
      for (const tline of meta.translatorDisplay.split('\n')) {
        lines.push(`  ${tline}`);
      }
    }

    if (meta.publishedPrint) lines.push(`published-print: "${meta.publishedPrint}"`);
    if (meta.price) lines.push(`price: "${meta.price}"`);
    if (meta.place) lines.push(`place: "${meta.place}"`);
    if (meta.serie) lines.push(`serie: "${meta.serie}"`);
    if (meta.originalTitle) lines.push(`original-title: "${meta.originalTitle}"`);

    if (meta.coverCredit) {
      lines.push('cover-credit: |');
      for (const cline of meta.coverCredit.split('\n')) {
        lines.push(`  ${cline}`);
      }
    }

    if (meta.criticalDisplay) {
      lines.push('critical-display: |');
      for (const cline of meta.criticalDisplay.split('\n')) {
        lines.push(`  ${cline}`);
      }
    }

    if (meta.funding) {
      lines.push('funding: |');
      for (const fline of meta.funding.split('\n')) {
        lines.push(`  ${fline}`);
      }
    }

    if (meta.editions) {
      lines.push('editions: |');
      for (const eline of meta.editions.split('\n')) {
        lines.push(`  ${eline}`);
      }
    }

    if (meta.coverNote) {
      lines.push('cover-note: |');
      for (const cline of meta.coverNote.split('\n')) {
        lines.push(`  ${cline}`);
      }
    }

    // Custom fields
    if (meta.customFields.length > 0 && meta.customFields.some(f => f.key && f.value)) {
      lines.push('');
      for (const field of meta.customFields) {
        if (field.key && field.value) {
          if (field.value.includes('\n')) {
            lines.push(`${field.key}: |`);
            for (const fline of field.value.split('\n')) {
              lines.push(`  ${fline}`);
            }
          } else {
            lines.push(`${field.key}: "${field.value}"`);
          }
        }
      }
    }

    lines.push('---');
    return lines.join('\n');
  }

  // Parse project settings JSON (EPUB export options, separate from Pandoc YAML metadata)
  interface ProjectSettings {
    navReorder?: boolean;
    toc?: boolean;
    tocDepth?: number;
    tocTitle?: string;
  }

  function parseSettings(json: string | undefined): ProjectSettings {
    if (!json) return {};
    try {
      return JSON.parse(json) as ProjectSettings;
    } catch {
      return {};
    }
  }

  function toSettingsJson(meta: ProjectMetadata): string {
    const settings: ProjectSettings = {
      navReorder: meta.navReorder,
      toc: meta.toc,
      tocDepth: meta.tocDepth,
      tocTitle: meta.tocTitle,
    };
    return JSON.stringify(settings);
  }

  // Load metadata from project
  function loadMetadata(): void {
    if ($selectedProject?.metadata) {
      const parsed = parseYaml($selectedProject.metadata);
      const settings = parseSettings($selectedProject.settings);
      metadata = {
        ...defaultMetadata,
        ...parsed,
        // Load navReorder from project settings (not from YAML)
        navReorder: settings.navReorder ?? defaultMetadata.navReorder,
        toc: settings.toc ?? defaultMetadata.toc,
        tocDepth: settings.tocDepth ?? defaultMetadata.tocDepth,
        tocTitle: settings.tocTitle ?? defaultMetadata.tocTitle,
        // Ensure arrays are properly set
        creators: parsed.creators?.length ? parsed.creators : defaultMetadata.creators,
        identifiers: parsed.identifiers?.length ? parsed.identifiers : defaultMetadata.identifiers,
        subjects: parsed.subjects?.length ? parsed.subjects : defaultMetadata.subjects,
                accessModes: parsed.accessModes?.length ? parsed.accessModes : defaultMetadata.accessModes,
        accessModeSufficient: parsed.accessModeSufficient?.length ? parsed.accessModeSufficient : defaultMetadata.accessModeSufficient,
        accessibilityHazards: parsed.accessibilityHazards?.length ? parsed.accessibilityHazards : defaultMetadata.accessibilityHazards,
        accessibilityFeatures: parsed.accessibilityFeatures?.length ? parsed.accessibilityFeatures : defaultMetadata.accessibilityFeatures,
        customFields: parsed.customFields || []
      };
      // Use project title/author as defaults if not in metadata
      if (!metadata.title && $selectedProject.title) {
        metadata.title = $selectedProject.title;
      }
      if (metadata.creators.length === 0 || !metadata.creators[0].name) {
        if ($selectedProject.author) {
          metadata.creators = [{ name: $selectedProject.author, role: 'author' }];
        }
      }
    } else if ($selectedProject) {
      const settings = parseSettings($selectedProject.settings);
      // Initialize with project info
      metadata = {
        ...defaultMetadata,
        navReorder: settings.navReorder ?? defaultMetadata.navReorder,
        toc: settings.toc ?? defaultMetadata.toc,
        tocDepth: settings.tocDepth ?? defaultMetadata.tocDepth,
        tocTitle: settings.tocTitle ?? defaultMetadata.tocTitle,
        title: $selectedProject.title || '',
        creators: $selectedProject.author
          ? [{ name: $selectedProject.author, role: 'author' }]
          : [{ name: '', role: 'author' }]
      };
    }
    hasChanges = false;
  }

  // Save metadata to project
  async function saveMetadata(): Promise<void> {
    if (!$selectedProject) return;

    isSaving = true;
    try {
      const yaml = toYaml(metadata);
      const settings = toSettingsJson(metadata);
      await appActions.updateProject($selectedProject.id, {
        metadata: yaml,
        settings,
        title: metadata.title || $selectedProject.title,
        author: metadata.creators[0]?.name || $selectedProject.author
      });
      hasChanges = false;
    } catch (error) {
      console.error('Failed to save metadata:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      isSaving = false;
    }
  }

  function markChanged(): void {
    hasChanges = true;
  }

  // Creator management
  function addCreator(): void {
    metadata.creators = [...metadata.creators, { name: '', role: 'author' }];
    markChanged();
  }

  function removeCreator(index: number): void {
    metadata.creators = metadata.creators.filter((_, i) => i !== index);
    if (metadata.creators.length === 0) {
      metadata.creators = [{ name: '', role: 'author' }];
    }
    markChanged();
  }

  // Contributor management
  function addContributor(): void {
    metadata.contributors = [...metadata.contributors, { name: '', role: 'translator' }];
    markChanged();
  }

  function removeContributor(index: number): void {
    metadata.contributors = metadata.contributors.filter((_, i) => i !== index);
    markChanged();
  }

  // Identifier management
  function addIdentifier(): void {
    metadata.identifiers = [...metadata.identifiers, { scheme: 'ISBN', text: '' }];
    markChanged();
  }

  function removeIdentifier(index: number): void {
    metadata.identifiers = metadata.identifiers.filter((_, i) => i !== index);
    if (metadata.identifiers.length === 0) {
      metadata.identifiers = [{ scheme: 'ISBN', text: '' }];
    }
    markChanged();
  }

  // Subject management
  function addSubject(): void {
    metadata.subjects = [...metadata.subjects, { text: '', authority: 'BISAC', term: '' }];
    markChanged();
  }

  function removeSubject(index: number): void {
    metadata.subjects = metadata.subjects.filter((_, i) => i !== index);
    if (metadata.subjects.length === 0) {
      metadata.subjects = [{ text: '', authority: 'BISAC', term: '' }];
    }
    markChanged();
  }

  // Custom field management
  function addCustomField(): void {
    metadata.customFields = [...metadata.customFields, { key: '', value: '' }];
    markChanged();
  }

  function removeCustomField(index: number): void {
    metadata.customFields = metadata.customFields.filter((_, i) => i !== index);
    markChanged();
  }

  // Toggle functions for checkboxes
  function toggleAccessMode(mode: string): void {
    if (metadata.accessModes.includes(mode)) {
      metadata.accessModes = metadata.accessModes.filter(m => m !== mode);
    } else {
      metadata.accessModes = [...metadata.accessModes, mode];
    }
    markChanged();
  }

  function toggleAccessModeSufficient(mode: string): void {
    if (metadata.accessModeSufficient.includes(mode)) {
      metadata.accessModeSufficient = metadata.accessModeSufficient.filter(m => m !== mode);
    } else {
      metadata.accessModeSufficient = [...metadata.accessModeSufficient, mode];
    }
    markChanged();
  }

  function toggleAccessibilityHazard(hazard: string): void {
    if (metadata.accessibilityHazards.includes(hazard)) {
      metadata.accessibilityHazards = metadata.accessibilityHazards.filter(h => h !== hazard);
    } else {
      metadata.accessibilityHazards = [...metadata.accessibilityHazards, hazard];
    }
    markChanged();
  }

  function toggleAccessibilityFeature(feature: string): void {
    if (metadata.accessibilityFeatures.includes(feature)) {
      metadata.accessibilityFeatures = metadata.accessibilityFeatures.filter(f => f !== feature);
    } else {
      metadata.accessibilityFeatures = [...metadata.accessibilityFeatures, feature];
    }
    markChanged();
  }

  function handleClose(): void {
    if (hasChanges) {
      if (!confirm('Vous avez des modifications non sauvegardees. Voulez-vous vraiment fermer ?')) {
        return;
      }
    }
    dispatch('close');
  }

  // File input ref for import
  let fileInputRef: HTMLInputElement;

  // Import YAML from file
  function handleImportClick(): void {
    fileInputRef?.click();
  }

  async function handleFileImport(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsed = parseYaml(text);

      // Merge parsed data with defaults
      metadata = {
        ...defaultMetadata,
        ...parsed,
        creators: parsed.creators?.length ? parsed.creators : defaultMetadata.creators,
        identifiers: parsed.identifiers?.length ? parsed.identifiers : defaultMetadata.identifiers,
        subjects: parsed.subjects?.length ? parsed.subjects : defaultMetadata.subjects,
                accessModes: parsed.accessModes?.length ? parsed.accessModes : defaultMetadata.accessModes,
        accessModeSufficient: parsed.accessModeSufficient?.length ? parsed.accessModeSufficient : defaultMetadata.accessModeSufficient,
        accessibilityHazards: parsed.accessibilityHazards?.length ? parsed.accessibilityHazards : defaultMetadata.accessibilityHazards,
        accessibilityFeatures: parsed.accessibilityFeatures?.length ? parsed.accessibilityFeatures : defaultMetadata.accessibilityFeatures,
        customFields: parsed.customFields || []
      };

      hasChanges = true;
      console.log('✅ YAML imported successfully');
    } catch (error) {
      console.error('Failed to import YAML:', error);
      alert('Erreur lors de l\'import du fichier YAML');
    } finally {
      // Reset input
      input.value = '';
    }
  }

  // Export YAML to file
  function handleExport(): void {
    const yaml = toYaml(metadata);
    const blob = new Blob([yaml], { type: 'text/yaml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const filename = metadata.title
      ? `${metadata.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-metadata.yaml`
      : 'project-metadata.yaml';

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('✅ YAML exported:', filename);
  }

  // Cover image handling
  let coverImageInputRef: HTMLInputElement;
  let isUploadingCover: boolean = false;
  let showCoverPicker: boolean = false;

  // Find existing cover image in images store (reserved for future use)
  $: _existingCoverImage = $images.find(img =>
    img.filename.toLowerCase().includes('cover') ||
    metadata.coverImage === img.filename
  );

  // Get the cover image data URL for preview
  $: coverImagePreview = metadata.coverImage
    ? $images.find(img => img.filename === metadata.coverImage)?.dataUrl
    : null;

  // Handle cover image file upload
  async function handleCoverImageUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !$selectedProject) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('L\'image est trop volumineuse (max 10MB)');
      return;
    }

    isUploadingCover = true;

    try {
      // Read file as data URL
      const dataUrl = await readFileAsDataUrl(file);
      const dimensions = await getImageDimensions(dataUrl);

      // Use "cover" prefix for the filename
      const coverFilename = file.name.toLowerCase().startsWith('cover')
        ? file.name
        : `cover-${file.name}`;

      // Create image record
      const imageData: CreateImageInput = {
        projectId: $selectedProject.id,
        filename: coverFilename,
        mimeType: file.type,
        dataUrl,
        size: file.size,
        width: dimensions.width,
        height: dimensions.height,
        altText: 'Couverture'
      };

      await appActions.addImage(imageData);

      // Update metadata with the cover image filename
      metadata.coverImage = coverFilename;
      markChanged();

      console.log('✅ Cover image uploaded:', coverFilename);
    } catch (error) {
      console.error('Failed to upload cover image:', error);
      alert('Erreur lors de l\'upload de l\'image de couverture');
    } finally {
      isUploadingCover = false;
      if (input) input.value = '';
    }
  }

  // Read file as data URL
  function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  // Get image dimensions
  function getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = dataUrl;
    });
  }

  // Select cover from existing images
  function selectCoverImage(image: Image): void {
    metadata.coverImage = image.filename;
    showCoverPicker = false;
    markChanged();
  }

  // Remove cover image selection
  function removeCoverImage(): void {
    metadata.coverImage = '';
    markChanged();
  }

  // Tab management
  type ConfigTab = 'general' | 'metadata' | 'epub' | 'indesign';
  let activeTab: ConfigTab = 'general';

  // InDesign sub-tab management
  type InDesignSubTab = 'mapping' | 'typography' | 'pagelayout' | 'grep' | 'export' | 'templates';
  let indesignSubTab: InDesignSubTab = 'mapping';

  // InDesign mapping configuration
  interface InDesignMapping {
    // Paragraph styles
    h1: string;
    h2: string;
    h3: string;
    h4: string;
    h5: string;
    h6: string;
    quote: string;
    bulletlist: string;
    normal: string;
    // Character styles
    italic: string;
    bold: string;
    bolditalic: string;
    underline: string;
    smallcaps: string;
    subscript: string;
    superscript: string;
    strikethrough: string;
    note: string;
    // Options
    removeBlankPages: boolean;
  }

  let indesignMapping: InDesignMapping = {
    h1: 'H1',
    h2: 'H2',
    h3: 'H3',
    h4: 'H4',
    h5: 'H5',
    h6: 'H6',
    quote: 'Citation',
    bulletlist: 'Bulleted list',
    normal: 'Body text',
    italic: 'Italic',
    bold: 'Bold',
    bolditalic: 'Bold italic',
    underline: 'Underline',
    smallcaps: 'Small caps',
    subscript: 'Subscript',
    superscript: 'Superscript',
    strikethrough: 'Strikethrough',
    note: 'Footnote',
    removeBlankPages: true
  };

  // ==========================================
  // Typography configuration (typography.json)
  // ==========================================
  interface TypographyConfig {
    version: number;
    languageProfile: string;
    styles: {
      noteStyle: string;
      italicStyle: string;
      smallCapsStyle: string;
      capitalsStyle: string;
      superscriptStyle: string;
    };
    corrections: Record<string, any>;
    formatting: Record<string, any>;
  }

  const defaultTypographyConfig: TypographyConfig = {
    version: 1,
    languageProfile: 'fr-FR',
    styles: {
      noteStyle: 'Superscript',
      italicStyle: 'Italic',
      smallCapsStyle: 'Small Caps',
      capitalsStyle: 'Capitals',
      superscriptStyle: 'Superscript'
    },
    corrections: {
      moveNotes: true,
      convertEllipsis: true,
      replaceApostrophes: true,
      replaceDashes: true,
      fixIsolatedHyphens: true,
      fixValueRanges: true,
      fixDoubleSpaces: true,
      removeDoubleReturns: true,
      removeSpacesBeforePunctuation: true,
      removeSpacesStartParagraph: true,
      removeSpacesEndParagraph: true,
      removeTabs: true,
      formatEspaces: true,
      fixDashIncises: false,
      fixDashIncisesType: 0,
      fixTypoSpaces: true,
      fixTypoSpacesPunct: 0,
      fixTypoSpacesColon: 0,
      applyNoteStyle: true,
      applyItalicStyle: true,
      applyItalicExpressions: true,
      applyExposantStyle: true
    },
    formatting: {
      formatSiecles: true,
      formatOrdinaux: true,
      formatReferences: true,
      formatNumbers: true,
      addThousandsSep: true,
      thousandsSepType: 0,
      excludeYears: true,
      useComma: true
    }
  };

  let typographyConfig: TypographyConfig = JSON.parse(JSON.stringify(defaultTypographyConfig));
  let typographyConfigChanged = false;
  let isSavingTypography = false;

  // Available language profiles for typography
  const languageProfiles = [
    { value: 'fr-FR', label: 'Francais (France)' },
    { value: 'fr-CH', label: 'Francais (Suisse)' },
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-UK', label: 'English (UK)' },
    { value: 'de', label: 'Deutsch' },
    { value: 'es', label: 'Espanol' },
    { value: 'it', label: 'Italiano' },
    { value: 'pt', label: 'Portugues' }
  ];

  // ==========================================
  // PageLayout configuration (pagelayout.json)
  // ==========================================
  interface PageLayoutConfig {
    layout: {
      enableStyleAfter: boolean;
      triggerStyles: string[];
      targetStyle: string;
      applyMasterToLastPage: boolean;
      masterName: string;
    };
  }

  const defaultPageLayoutConfig: PageLayoutConfig = {
    layout: {
      enableStyleAfter: false,
      triggerStyles: [],
      targetStyle: '',
      applyMasterToLastPage: false,
      masterName: ''
    }
  };

  let pagelayoutConfig: PageLayoutConfig = JSON.parse(JSON.stringify(defaultPageLayoutConfig));
  let pagelayoutConfigChanged = false;
  let isSavingPagelayout = false;

  // Trigger style input for chip/tag UI

  let pagelayoutTriggerInput = '';

  // ==========================================
  // Grep configuration (grep.json)
  // ==========================================
  interface GrepRule {
    find: string;
    change: string;
    enabled: boolean;
    findParaStyle: string | null;
    findCharStyle: string | null;
    changeParaStyle: string | null;
    changeCharStyle: string | null;
  }

  interface GrepConfig {
    version: number;
    rules: GrepRule[];
  }

  const defaultGrepConfig: GrepConfig = {
    version: 2,
    rules: []
  };

  let grepConfig: GrepConfig = JSON.parse(JSON.stringify(defaultGrepConfig));
  let grepConfigChanged = false;
  let isSavingGrep = false;

  // ==========================================
  // Export configuration (export.json)
  // ==========================================
  interface ExportConfig {
    indesignEnabled: boolean;
    pdfEnabled: boolean;
    epubEnabled: boolean;
    pdfFormat: 'interactive' | 'print' | 'both';
    pdfPreset: string;
    epubFormat: 'epub2' | 'epub3' | 'both';
  }

  const defaultExportConfig: ExportConfig = {
    indesignEnabled: true,
    pdfEnabled: false,
    epubEnabled: false,
    pdfFormat: 'print',
    pdfPreset: '',
    epubFormat: 'epub3'
  };

  let exportConfig: ExportConfig = { ...defaultExportConfig };
  let exportConfigChanged = false;
  let isSavingExport = false;

  // ==========================================
  // General configuration (general.json)
  // ==========================================
  interface GeneralConfig {
    presetId: string;
    presetName: string;
    typographyEnabled: boolean;
    filePrefix: string;
  }

  const defaultGeneralConfig: GeneralConfig = {
    presetId: '',
    presetName: '',
    typographyEnabled: true,
    filePrefix: ''
  };

  let generalConfig: GeneralConfig = { ...defaultGeneralConfig };
  let generalConfigChanged = false;
  let isSavingGeneral = false;
  let isApplyingPreset = false;

  // Load presets for the dropdown (project mode only)
  $: if (!$presetMode && $selectedProject) {
    appActions.loadPresets();
  }

  async function handleApplyPreset(event: Event): Promise<void> {
    const select = event.target as HTMLSelectElement;
    const newPresetId = select.value;

    if (!$selectedProject) return;

    if (!newPresetId) {
      // Dissociate preset
      generalConfig.presetId = '';
      generalConfig.presetName = '';
      generalConfigChanged = true;
      return;
    }

    const preset = $presets.find(p => p.id === newPresetId);
    if (!preset) return;

    isApplyingPreset = true;
    try {
      await appActions.applyPresetToProject(newPresetId, $selectedProject.id);
      // Reload configs after applying preset
      generalConfig.presetId = newPresetId;
      generalConfig.presetName = preset.name;
      generalConfigChanged = false;
      // Reload all configs to reflect preset values
      loadIndesignMapping();
      loadIndesignConfigs();
      loadMetadata();
    } catch (error) {
      console.error('Failed to apply preset:', error);
    } finally {
      isApplyingPreset = false;
    }
  }

  let indesignMappingChanged = false;
  let isSavingIndesign = false;
  let indesignFileInputRef: HTMLInputElement;

  // InDesign template files (.indd) management
  let indesignTemplateInputRef: HTMLInputElement;
  let isUploadingIndesignTemplate = false;

  interface IndesignTemplateFile {
    id: string;
    filename: string;
    size: number;
  }

  $: indesignTemplateFiles = ($templates || [])
    .filter((t: any) => t.templateSection === 'indesign' && t.templateType === 'indesign' && t.filename?.endsWith('.indd'))
    .map((t: any): IndesignTemplateFile => ({ id: t.id, filename: t.filename, size: t.size || 0 }));

  // Load InDesign mapping from templates (mapping.json)
  async function loadIndesignMapping(): Promise<void> {
    if (!$selectedProject && !$presetMode) return;

    try {
      const tpls = $templates;
      const mappingTemplate = tpls.find(t => t.filename === 'mapping.json');

      if (mappingTemplate?.content) {
        const parsed = JSON.parse(mappingTemplate.content);
        indesignMapping = {
          h1: parsed.h1 || 'H1',
          h2: parsed.h2 || 'H2',
          h3: parsed.h3 || 'H3',
          h4: parsed.h4 || 'H4',
          h5: parsed.h5 || 'H5',
          h6: parsed.h6 || 'H6',
          quote: parsed.quote || 'Citation',
          bulletlist: parsed.bulletlist || 'Bulleted list',
          normal: parsed.normal || 'Body text',
          italic: parsed.italic || 'Italic',
          bold: parsed.bold || 'Bold',
          bolditalic: parsed.bolditalic || 'Bold italic',
          underline: parsed.underline || 'Underline',
          smallcaps: parsed.smallcaps || 'Small caps',
          subscript: parsed.subscript || 'Subscript',
          superscript: parsed.superscript || 'Superscript',
          strikethrough: parsed.strikethrough || 'Strikethrough',
          note: parsed.note || 'Footnote',
          removeBlankPages: parsed.removeBlankPages !== undefined ? parsed.removeBlankPages : true
        };
        indesignMappingChanged = false;
      }
    } catch (error) {
      console.warn('Could not load InDesign mapping:', error);
    }
  }

  // Load all InDesign configs from templates
  async function loadIndesignConfigs(): Promise<void> {
    if (!$selectedProject && !$presetMode) return;
    try {
      const tpls = $templates;

      // Typography
      const typoTemplate = tpls.find(t => t.filename === 'typography.json');
      if (typoTemplate?.content) {
        const parsed = JSON.parse(typoTemplate.content);
        const { layout: _ignoreLayout, ...rest } = parsed;
        typographyConfig = { ...defaultTypographyConfig, ...rest,
          styles: { ...defaultTypographyConfig.styles, ...(parsed.styles || {}) },
          corrections: { ...defaultTypographyConfig.corrections, ...(parsed.corrections || {}) },
          formatting: { ...defaultTypographyConfig.formatting, ...(parsed.formatting || {}) }
        };
        typographyConfigChanged = false;
      }

      // PageLayout
      const plTemplate = tpls.find(t => t.filename === 'pagelayout.json');
      if (plTemplate?.content) {
        const parsed = JSON.parse(plTemplate.content);
        pagelayoutConfig = { layout: { ...defaultPageLayoutConfig.layout, ...(parsed.layout || {}) } };
        pagelayoutConfigChanged = false;
      }

      // Grep
      const grepTemplate = tpls.find(t => t.filename === 'grep.json');
      if (grepTemplate?.content) {
        const parsed = JSON.parse(grepTemplate.content);
        grepConfig = { version: parsed.version || 2, rules: parsed.rules || [] };
        grepConfigChanged = false;
      }

      // Export
      const exportTemplate = tpls.find(t => t.filename === 'export.json');
      if (exportTemplate?.content) {
        const parsed = JSON.parse(exportTemplate.content);
        exportConfig = { ...defaultExportConfig, ...parsed };
        exportConfigChanged = false;
      }

      // General
      const generalTemplate = tpls.find(t => t.filename === 'general.json');
      if (generalTemplate?.content) {
        const parsed = JSON.parse(generalTemplate.content);
        generalConfig = { ...defaultGeneralConfig, ...parsed };
        generalConfigChanged = false;
      }
    } catch (error) {
      console.warn('Could not load InDesign configs:', error);
    }
  }

  // Save InDesign mapping as template (mapping.json)
  async function saveIndesignMapping(): Promise<void> {
    const entityId = $presetMode ? $selectedPreset?.id : $selectedProject?.id;
    if (!entityId) return;

    isSavingIndesign = true;
    try {
      const mappingJson = JSON.stringify(indesignMapping, null, '\t');

      // Check if mapping template already exists
      const existingTemplate = $templates.find(t => t.filename === 'mapping.json');

      if (existingTemplate) {
        // Update existing template
        await appActions.updateTemplate(existingTemplate.id, {
          content: mappingJson
        });
      } else {
        // Create new template
        const templateData: CreateTemplateInput = {
          projectId: $presetMode ? '' : entityId,
          presetId: $presetMode ? entityId : undefined,
          filename: 'mapping.json',
          content: mappingJson,
          templateType: 'config',
          templateSection: 'config',
          mimeType: 'application/json',
          size: new Blob([mappingJson]).size
        };
        await appActions.addTemplate(templateData);
      }

      indesignMappingChanged = false;
      console.log('InDesign mapping saved successfully');
    } catch (error) {
      console.error('Failed to save InDesign mapping:', error);
      alert('Erreur lors de la sauvegarde du mapping InDesign');
    } finally {
      isSavingIndesign = false;
    }
  }

  // Import InDesign mapping from file (.mdconfig or .json)
  function handleIndesignImportClick(): void {
    indesignFileInputRef?.click();
  }

  async function handleIndesignFileImport(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    try {
      const content = await file.text();
      const parsed = JSON.parse(content);

      // Update mapping with imported values
      indesignMapping = {
        h1: parsed.h1 || indesignMapping.h1,
        h2: parsed.h2 || indesignMapping.h2,
        h3: parsed.h3 || indesignMapping.h3,
        h4: parsed.h4 || indesignMapping.h4,
        h5: parsed.h5 || indesignMapping.h5,
        h6: parsed.h6 || indesignMapping.h6,
        quote: parsed.quote || indesignMapping.quote,
        bulletlist: parsed.bulletlist || indesignMapping.bulletlist,
        normal: parsed.normal || indesignMapping.normal,
        italic: parsed.italic || indesignMapping.italic,
        bold: parsed.bold || indesignMapping.bold,
        bolditalic: parsed.bolditalic || indesignMapping.bolditalic,
        underline: parsed.underline || indesignMapping.underline,
        smallcaps: parsed.smallcaps || indesignMapping.smallcaps,
        subscript: parsed.subscript || indesignMapping.subscript,
        superscript: parsed.superscript || indesignMapping.superscript,
        strikethrough: parsed.strikethrough || indesignMapping.strikethrough,
        note: parsed.note || indesignMapping.note,
        removeBlankPages: parsed.removeBlankPages !== undefined ? parsed.removeBlankPages : indesignMapping.removeBlankPages
      };

      indesignMappingChanged = true;
      console.log('InDesign mapping imported from:', file.name);
    } catch (error) {
      console.error('Failed to import InDesign mapping:', error);
      alert('Erreur lors de l\'import du mapping. Verifiez que le fichier est au format JSON valide.');
    }

    // Reset input
    input.value = '';
  }

  // Export InDesign mapping to file
  function exportIndesignMapping(): void {
    const mappingJson = JSON.stringify(indesignMapping, null, '\t');
    const blob = new Blob([mappingJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'mapping.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Generic JSON config file input ref (for typography, pagelayout, grep, export imports)
  let configFileInputRef: HTMLInputElement;
  let pendingConfigImportType: string = '';

  function handleConfigImportClick(configType: string): void {
    pendingConfigImportType = configType;
    configFileInputRef?.click();
  }

  async function handleConfigFileImport(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    try {
      const content = await file.text();
      const parsed = JSON.parse(content);

      switch (pendingConfigImportType) {
        case 'typography': {
          const { layout: _ignoreLayout, ...rest } = parsed;
          typographyConfig = {
            ...defaultTypographyConfig, ...rest,
            styles: { ...defaultTypographyConfig.styles, ...(parsed.styles || {}) },
            corrections: { ...defaultTypographyConfig.corrections, ...(parsed.corrections || {}) },
            formatting: { ...defaultTypographyConfig.formatting, ...(parsed.formatting || {}) }
          };
          typographyConfigChanged = true;
          break;
        }
        case 'pagelayout':
          pagelayoutConfig = { layout: { ...defaultPageLayoutConfig.layout, ...(parsed.layout || {}) } };
          pagelayoutConfigChanged = true;
          break;
        case 'grep':
          grepConfig = { version: parsed.version || 2, rules: parsed.rules || [] };
          grepConfigChanged = true;
          break;
        case 'export':
          exportConfig = { ...defaultExportConfig, ...parsed };
          exportConfigChanged = true;
          break;
        case 'general':
          generalConfig = { ...defaultGeneralConfig, ...parsed };
          generalConfigChanged = true;
          break;
      }

      console.log(`${pendingConfigImportType} config imported from:`, file.name);
    } catch (error) {
      console.error(`Failed to import ${pendingConfigImportType} config:`, error);
      alert('Erreur lors de l\'import. Verifiez que le fichier est au format JSON valide.');
    }

    input.value = '';
  }

  function exportConfigFile(configType: string): void {
    let data: string;
    let filename: string;

    switch (configType) {
      case 'typography':
        data = JSON.stringify(typographyConfig, null, '\t');
        filename = 'typography.json';
        break;
      case 'pagelayout':
        data = JSON.stringify(pagelayoutConfig, null, '\t');
        filename = 'pagelayout.json';
        break;
      case 'grep':
        data = JSON.stringify(grepConfig, null, '\t');
        filename = 'grep.json';
        break;
      case 'export':
        data = JSON.stringify(exportConfig, null, '\t');
        filename = 'export.json';
        break;
      case 'general':
        data = JSON.stringify(generalConfig, null, '\t');
        filename = 'general.json';
        break;
      default:
        return;
    }

    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ==========================================
  // Save/Load helpers for new InDesign configs
  // ==========================================

  /** Generic save: upsert a template by filename (works for both project and preset) */
  async function saveConfigTemplate(filename: string, content: string): Promise<void> {
    const entityId = $presetMode ? $selectedPreset?.id : $selectedProject?.id;
    if (!entityId) return;
    const existing = $templates.find(t => t.filename === filename);
    if (existing) {
      await appActions.updateTemplate(existing.id, { content });
    } else {
      await appActions.addTemplate({
        projectId: $presetMode ? '' : entityId,
        presetId: $presetMode ? entityId : undefined,
        filename,
        content,
        templateType: 'config',
        templateSection: 'config',
        mimeType: 'application/json',
        size: new Blob([content]).size
      });
    }
  }

  // Save Typography config
  async function saveTypographyConfig(): Promise<void> {
    isSavingTypography = true;
    try {
      await saveConfigTemplate('typography.json', JSON.stringify(typographyConfig, null, '\t'));
      typographyConfigChanged = false;
    } catch (error) {
      console.error('Failed to save typography config:', error);
      alert('Erreur lors de la sauvegarde de la configuration typographie');
    } finally {
      isSavingTypography = false;
    }
  }

  // Save PageLayout config
  async function savePagelayoutConfig(): Promise<void> {
    isSavingPagelayout = true;
    try {
      await saveConfigTemplate('pagelayout.json', JSON.stringify(pagelayoutConfig, null, '\t'));
      pagelayoutConfigChanged = false;
    } catch (error) {
      console.error('Failed to save pagelayout config:', error);
      alert('Erreur lors de la sauvegarde de la configuration mise en page');
    } finally {
      isSavingPagelayout = false;
    }
  }

  // Save Grep config
  async function saveGrepConfig(): Promise<void> {
    isSavingGrep = true;
    try {
      await saveConfigTemplate('grep.json', JSON.stringify(grepConfig, null, '\t'));
      grepConfigChanged = false;
    } catch (error) {
      console.error('Failed to save grep config:', error);
      alert('Erreur lors de la sauvegarde de la configuration GREP');
    } finally {
      isSavingGrep = false;
    }
  }

  // Save Export config
  async function saveExportConfig(): Promise<void> {
    isSavingExport = true;
    try {
      await saveConfigTemplate('export.json', JSON.stringify(exportConfig, null, '\t'));
      exportConfigChanged = false;
    } catch (error) {
      console.error('Failed to save export config:', error);
      alert('Erreur lors de la sauvegarde de la configuration export');
    } finally {
      isSavingExport = false;
    }
  }

  // Save General config
  async function saveGeneralConfig(): Promise<void> {
    isSavingGeneral = true;
    try {
      await saveConfigTemplate('general.json', JSON.stringify(generalConfig, null, '\t'));
      generalConfigChanged = false;
    } catch (error) {
      console.error('Failed to save general config:', error);
      alert('Erreur lors de la sauvegarde de la configuration generale');
    } finally {
      isSavingGeneral = false;
    }
  }

  // InDesign template file (.indd) management
  async function handleIndesignTemplateUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    const entityId = $presetMode ? $selectedPreset?.id : $selectedProject?.id;
    if (!files || !entityId) return;

    isUploadingIndesignTemplate = true;
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.name.endsWith('.indd')) continue;

        // Read file as base64
        const base64Content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(',')[1] || '';
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Check if template with same filename already exists
        const existing = $templates.find(
          (t: any) => t.templateSection === 'indesign' && t.templateType === 'indesign' && t.filename === file.name
        );

        if (existing) {
          await appActions.updateTemplate(existing.id, { content: base64Content, size: file.size });
        } else {
          await appActions.addTemplate({
            projectId: $presetMode ? '' : entityId,
            presetId: $presetMode ? entityId : undefined,
            filename: file.name,
            templateType: 'indesign',
            templateSection: 'indesign',
            mimeType: 'application/x-indesign',
            content: base64Content,
            size: file.size
          });
        }
      }
      // Reload templates
      if ($presetMode && $selectedPreset) {
        await appActions.selectPreset($selectedPreset);
      } else if ($selectedProject) {
        await appActions.loadTemplates($selectedProject.id);
      }
    } catch (error) {
      console.error('Failed to upload InDesign template:', error);
      alert('Erreur lors de l\'import du template InDesign');
    } finally {
      isUploadingIndesignTemplate = false;
      if (input) input.value = '';
    }
  }

  async function deleteIndesignTemplate(templateId: string): Promise<void> {
    if (!confirm('Supprimer ce template InDesign ?')) return;
    try {
      await appActions.deleteTemplate(templateId);
      if ($presetMode && $selectedPreset) {
        await appActions.selectPreset($selectedPreset);
      } else if ($selectedProject) {
        await appActions.loadTemplates($selectedProject.id);
      }
    } catch (error) {
      console.error('Failed to delete InDesign template:', error);
    }
  }

  // Grep rule management
  function addGrepRule(): void {
    grepConfig.rules = [...grepConfig.rules, {
      find: '',
      change: '',
      enabled: true,
      findParaStyle: null,
      findCharStyle: null,
      changeParaStyle: null,
      changeCharStyle: null
    }];
    grepConfigChanged = true;
  }

  function removeGrepRule(index: number): void {
    grepConfig.rules = grepConfig.rules.filter((_, i) => i !== index);
    grepConfigChanged = true;
  }

  // PageLayout trigger style management (chip/tag UI)
  function addPagelayoutTriggerStyle(): void {
    const val = pagelayoutTriggerInput.trim();
    if (val && !pagelayoutConfig.layout.triggerStyles.includes(val)) {
      pagelayoutConfig.layout.triggerStyles = [...pagelayoutConfig.layout.triggerStyles, val];
      pagelayoutConfigChanged = true;
    }
    pagelayoutTriggerInput = '';
  }

  function removePagelayoutTriggerStyle(style: string): void {
    pagelayoutConfig.layout.triggerStyles = pagelayoutConfig.layout.triggerStyles.filter(s => s !== style);
    pagelayoutConfigChanged = true;
  }

  // Load on mount or when switching projects/presets (not on every store update)
  let loadedProjectId: string | null = null;
  let loadedPresetId: string | null = null;
  let loadedMetadataSnapshot: string | null = null;
  $: if ($selectedProject && !$presetMode && $selectedProject.id !== loadedProjectId) {
    loadedProjectId = $selectedProject.id;
    loadedMetadataSnapshot = $selectedProject.metadata ?? null;
    loadMetadata();
    loadIndesignMapping();
    loadIndesignConfigs();
  }
  // Reload metadata when it changes externally (e.g. background hydration from Git)
  // but only if the user has no unsaved changes in the panel.
  $: if ($selectedProject && !$presetMode && loadedProjectId === $selectedProject.id
      && $selectedProject.metadata !== loadedMetadataSnapshot && !hasChanges) {
    loadedMetadataSnapshot = $selectedProject.metadata ?? null;
    loadMetadata();
  }
  // Load configs when a preset is selected for editing
  $: if ($presetMode && $selectedPreset && $selectedPreset.id !== loadedPresetId) {
    loadedPresetId = $selectedPreset.id;
    loadIndesignMapping();
    loadIndesignConfigs();
  }

  // Auto-detect cover image on load
  $: if ($selectedProject && !metadata.coverImage && $images.length > 0) {
    const coverImg = $images.find(img => img.filename.toLowerCase().includes('cover'));
    if (coverImg) {
      metadata.coverImage = coverImg.filename;
    }
  }
</script>

<div class="config-panel">
  <div class="config-header">
    <h2>{$presetMode ? ($selectedPreset?.name || 'Preset') : 'Configuration du projet'}</h2>
    <button class="btn-close" on:click={handleClose} title="Fermer">
      ✕
    </button>
  </div>

  <div class="config-tabs">
    <button
      class="config-tab"
      class:active={activeTab === 'general'}
      on:click={() => activeTab = 'general'}
    >
      General
    </button>
    {#if !$presetMode}
    <button
      class="config-tab"
      class:active={activeTab === 'metadata'}
      on:click={() => activeTab = 'metadata'}
    >
      Metadonnees
    </button>
    <button
      class="config-tab"
      class:active={activeTab === 'epub'}
      on:click={() => activeTab = 'epub'}
    >
      EPUB
    </button>
    {/if}
    <button
      class="config-tab"
      class:active={activeTab === 'indesign'}
      on:click={() => activeTab = 'indesign'}
    >
      InDesign
    </button>
  </div>

  <div class="config-content">
   {#if activeTab === 'general'}
    <!-- General Configuration -->
    <section class="config-section">
      <h3>Configuration generale</h3>

      <div class="form-group">
        <label for="filePrefix">Prefixe de fichier</label>
        <input
          id="filePrefix"
          type="text"
          bind:value={generalConfig.filePrefix}
          on:input={() => { generalConfigChanged = true; }}
          placeholder="nom-du-projet"
        />
        <span class="form-hint">Utilise pour nommer le dossier du projet dans AutomaticBook</span>
      </div>

      <div class="form-group checkbox-group">
        <label class="checkbox-label">
          <input
            type="checkbox"
            bind:checked={generalConfig.typographyEnabled}
            on:change={() => { generalConfigChanged = true; }}
          />
          Activer la correction typographique (SuperScript)
        </label>
        <span class="form-hint">Active le script de correction typographique lors de la generation InDesign</span>
      </div>
    </section>

    {#if !$presetMode}
    <section class="config-section">
      <h3>Preset</h3>
      <div class="form-group">
        <label for="presetSelect">Associer un preset</label>
        <select
          id="presetSelect"
          value={generalConfig.presetId || ''}
          on:change={handleApplyPreset}
          disabled={isApplyingPreset}
        >
          <option value="">Aucun</option>
          {#each $presets as preset (preset.id)}
            <option value={preset.id}>{preset.name}</option>
          {/each}
        </select>
        {#if isApplyingPreset}
          <span class="form-hint">Application du preset en cours...</span>
        {:else if generalConfig.presetId}
          <span class="form-hint">Les configs du preset ont ete copiees. Les templates du preset sont partages.</span>
        {:else}
          <span class="form-hint">Associer un preset pour utiliser ses templates et configs par defaut</span>
        {/if}
      </div>
    </section>
    {/if}

   {:else if activeTab === 'metadata'}
    <!-- General Info -->
    <section class="config-section">
      <h3>Informations generales</h3>

      <div class="form-group">
        <label for="title">Titre</label>
        <input id="title" type="text" bind:value={metadata.title} on:input={markChanged} placeholder="Titre du livre" />
      </div>

      <div class="form-group">
        <label for="subtitle">Sous-titre</label>
        <input id="subtitle" type="text" bind:value={metadata.subtitle} on:input={markChanged} placeholder="Sous-titre (optionnel)" />
      </div>

      <div class="form-group">
        <label for="originalTitle">Titre original</label>
        <input id="originalTitle" type="text" bind:value={metadata.originalTitle} on:input={markChanged} placeholder="Titre dans la langue originale" />
      </div>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="lang">Langue</label>
          {#if isCustomLang}
            <div class="custom-lang-row">
              <input id="lang" type="text" bind:value={metadata.lang} on:input={markChanged} placeholder="xx" style="max-width: 90px;" />
              <button class="btn-icon btn-remove" on:click={() => { customLang = false; metadata.lang = ''; markChanged(); }} title="Retour au menu">x</button>
            </div>
          {:else}
            <select id="lang" bind:value={metadata.lang} on:change={(e) => { if (metadata.lang === '__other__') { customLang = true; metadata.lang = ''; } markChanged(); }}>
              {#each languages as l}
                <option value={l.value}>{l.label}</option>
              {/each}
              <option value="__other__">Autre...</option>
            </select>
          {/if}
        </div>
        <div class="form-group flex-1">
          <label for="originalLanguage">Langue originale</label>
          {#if isCustomOrigLang}
            <div class="custom-lang-row">
              <input id="originalLanguage" type="text" bind:value={metadata.originalLanguage} on:input={markChanged} placeholder="xx" style="max-width: 90px;" />
              <button class="btn-icon btn-remove" on:click={() => { customOrigLang = false; metadata.originalLanguage = ''; markChanged(); }} title="Retour au menu">x</button>
            </div>
          {:else}
            <select id="originalLanguage" bind:value={metadata.originalLanguage} on:change={(e) => { if (metadata.originalLanguage === '__other__') { customOrigLang = true; metadata.originalLanguage = ''; } markChanged(); }}>
              <option value="">--</option>
              {#each languages as l}
                <option value={l.value}>{l.label}</option>
              {/each}
              <option value="__other__">Autre...</option>
            </select>
          {/if}
        </div>
      </div>
    </section>

    <!-- Description -->
    <section class="config-section">
      <h3>Description</h3>
      <div class="form-group">
        <textarea id="description" bind:value={metadata.description} on:input={markChanged} placeholder="Resume, quatrieme de couverture..." rows="5"></textarea>
      </div>
    </section>

    <!-- Creators (primary: author/editor) -->
    <section class="config-section">
      <h3>Auteurs</h3>

      {#each metadata.creators as creator, index}
        <div class="creator-row">
          <input type="text" bind:value={creator.name} on:input={markChanged} placeholder="Nom" class="creator-name" />
          <select bind:value={creator.role} on:change={markChanged} class="creator-role">
            {#each creatorRoles as role}
              <option value={role.value}>{role.label}</option>
            {/each}
          </select>
          <button class="btn-icon btn-remove" on:click={() => removeCreator(index)} title="Supprimer" disabled={metadata.creators.length === 1 && !creator.name}>x</button>
        </div>
      {/each}

      <button class="btn btn-secondary btn-add" on:click={addCreator}>
        + Ajouter un auteur
      </button>
    </section>

    <!-- Contributors (secondary roles) -->
    <section class="config-section">
      <h3>Contributeurs</h3>

      {#each metadata.contributors as contributor, index}
        <div class="creator-row">
          <input type="text" bind:value={contributor.name} on:input={markChanged} placeholder="Nom" class="creator-name" />
          <select bind:value={contributor.role} on:change={markChanged} class="creator-role">
            {#each contributorRoles as role}
              <option value={role.value}>{role.label}</option>
            {/each}
          </select>
          <button class="btn-icon btn-remove" on:click={() => removeContributor(index)} title="Supprimer">x</button>
        </div>
      {/each}

      <button class="btn btn-secondary btn-add" on:click={addContributor}>
        + Ajouter un contributeur
      </button>

      <div class="form-group" style="margin-top: 1rem;">
        <label for="translatorDisplay">Mention traducteur (affichage)</label>
        <textarea id="translatorDisplay" bind:value={metadata.translatorDisplay} on:input={markChanged} placeholder="Ex: par le collectif Senonevero,&#10;traduction revue par..." rows="3"></textarea>
        <span class="form-hint">Texte affiche pour crediter les traducteurs</span>
      </div>

      <div class="form-group">
        <label for="criticalDisplay">Mention edition critique</label>
        <textarea id="criticalDisplay" bind:value={metadata.criticalDisplay} on:input={markChanged} placeholder="Ex: Edition critique etablie par..." rows="3"></textarea>
        <span class="form-hint">Texte affiche pour crediter l'edition critique</span>
      </div>
    </section>

    <!-- Publisher Section -->
    <section class="config-section">
      <h3>Publication</h3>

      <div class="form-group">
        <label for="publisher">Editeur</label>
        <input id="publisher" type="text" bind:value={metadata.publisher} on:input={markChanged} placeholder="Nom de l'editeur" />
      </div>

      <div class="form-group">
        <label for="rights">Droits / Copyright</label>
        <textarea id="rights" bind:value={metadata.rights} on:input={markChanged} placeholder="Ex: Edition originale publiee par...&#10;Copyright 2024..." rows="3"></textarea>
      </div>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="date">Date (YYYY-MM)</label>
          <input id="date" type="text" bind:value={metadata.date} on:input={markChanged} placeholder="2025-10" />
        </div>
        <div class="form-group flex-1">
          <label for="publishedPrint">Date imprime</label>
          <input id="publishedPrint" type="text" bind:value={metadata.publishedPrint} on:input={markChanged} placeholder="Ex: septembre 2025" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="price">Prix</label>
          <input id="price" type="text" bind:value={metadata.price} on:input={markChanged} placeholder="Ex: 24" />
        </div>
        <div class="form-group flex-1">
          <label for="place">Lieu de publication</label>
          <input id="place" type="text" bind:value={metadata.place} on:input={markChanged} placeholder="Ex: Geneve, Paris" />
        </div>
      </div>

      <div class="form-group">
        <label for="serie">Collection / Serie</label>
        <input id="serie" type="text" bind:value={metadata.serie} on:input={markChanged} placeholder="Ex: rupture" />
      </div>
    </section>

    <!-- Textes -->
    <section class="config-section">
      <h3>Textes</h3>

      <div class="form-group">
        <label for="funding">Financement</label>
        <textarea id="funding" bind:value={metadata.funding} on:input={markChanged} placeholder="Ex: Publie avec le soutien de..." rows="3"></textarea>
      </div>

      <div class="form-group">
        <label for="editions">Editions</label>
        <textarea id="editions" bind:value={metadata.editions} on:input={markChanged} placeholder="Ex: Premiere edition: 2020..." rows="3"></textarea>
      </div>

      <div class="form-group">
        <label for="dedication">Dedicace</label>
        <textarea id="dedication" bind:value={metadata.dedication} on:input={markChanged} placeholder="Remerciements, dedicace..." rows="3"></textarea>
      </div>

      <div class="form-group">
        <label for="coverCredit">Credit couverture</label>
        <textarea id="coverCredit" bind:value={metadata.coverCredit} on:input={markChanged} placeholder="Ex: Photo de couverture par..." rows="3"></textarea>
      </div>
    </section>

    <!-- Subjects with BISAC picker -->
    <section class="config-section">
      <h3>Categories / Sujets</h3>

      {#each metadata.subjects as subject, index}
        <div class="subject-block">
          <div class="subject-row">
            <select bind:value={subject.authority} on:change={markChanged} class="subject-authority">
              {#each subjectAuthorities as auth}
                <option value={auth}>{auth}</option>
              {/each}
            </select>
            <button class="btn-icon btn-remove" on:click={() => removeSubject(index)} title="Supprimer">x</button>
          </div>

          {#if subject.authority === 'BISAC'}
            <!-- BISAC interactive picker -->
            <div class="bisac-picker">
              {#if subject.term}
                <!-- Selected BISAC: show pill -->
                <div class="bisac-pill" on:click={() => { ensureBisacLoaded(); bisacOpenId = index; subject.term = ''; subject.text = ''; markChanged(); }}>
                  <span class="bisac-code">{subject.term}</span>
                  <span class="bisac-desc">{subject.text}</span>
                  <button class="btn-icon btn-remove" on:click|stopPropagation={() => { subject.term = ''; subject.text = ''; markChanged(); }}>x</button>
                </div>
              {:else}
                <!-- BISAC search input -->
                <input
                  type="text"
                  class="bisac-search"
                  value={bisacSearches[index] || ''}
                  on:focus={() => { ensureBisacLoaded(); bisacOpenId = index; }}
                  on:input={(e) => { bisacSearches[index] = e.currentTarget.value; bisacSearches = bisacSearches; bisacOpenId = index; }}
                  on:keydown={(e) => { if (e.key === 'Enter') { if (activeBisacFiltered.length > 0) selectBisac(index, activeBisacFiltered[0]); } if (e.key === 'Escape') bisacOpenId = null; }}
                  on:blur={() => setTimeout(() => { if (bisacOpenId === index) bisacOpenId = null; }, 150)}
                  placeholder="Rechercher une categorie BISAC..."
                />
                {#if bisacOpenId === index}
                  <div class="bisac-dropdown">
                    {#each activeBisacFiltered as cat}
                      <div class="bisac-option" on:mousedown|preventDefault={() => selectBisac(index, cat)}>
                        <span class="bisac-code">{cat.code}</span>
                        <span class="bisac-desc">{cat.desc}</span>
                      </div>
                    {:else}
                      <div class="bisac-no-results">Aucun resultat</div>
                    {/each}
                  </div>
                {/if}
              {/if}
            </div>
          {:else}
            <!-- Non-BISAC: text + code inputs -->
            <div class="subject-details">
              <input type="text" bind:value={subject.text} on:input={markChanged} placeholder="Sujet" class="subject-text" />
              <input type="text" bind:value={subject.term} on:input={markChanged} placeholder="Code" class="subject-term" />
            </div>
          {/if}
        </div>
      {/each}

      <button class="btn btn-secondary btn-add" on:click={addSubject}>
        + Ajouter une categorie
      </button>
    </section>

    <!-- Identifiers (unified) -->
    <section class="config-section">
      <h3>Identifiants</h3>

      {#each metadata.identifiers as identifier, index}
        <div class="identifier-row">
          <select bind:value={identifier.scheme} on:change={markChanged} class="identifier-scheme">
            {#each identifierSchemes as scheme}
              <option value={scheme.value}>{scheme.label}</option>
            {/each}
          </select>
          <input type="text" bind:value={identifier.text} on:input={markChanged} placeholder="Ex: 978-2-1234-5678-9" class="identifier-text" />
          <button class="btn-icon btn-remove" on:click={() => removeIdentifier(index)} title="Supprimer" disabled={metadata.identifiers.length === 1}>x</button>
        </div>
      {/each}

      <button class="btn btn-secondary btn-add" on:click={addIdentifier}>
        + Ajouter un identifiant
      </button>
    </section>

    <!-- Custom Fields -->
    <section class="config-section">
      <h3>Champs personnalises</h3>

      {#each metadata.customFields as field, index}
        <div class="custom-field-row">
          <input type="text" bind:value={field.key} on:input={markChanged} placeholder="Cle" class="custom-key" />
          <input type="text" bind:value={field.value} on:input={markChanged} placeholder="Valeur" class="custom-value" />
          <button class="btn-icon btn-remove" on:click={() => removeCustomField(index)} title="Supprimer">x</button>
        </div>
      {/each}

      <button class="btn btn-secondary btn-add" on:click={addCustomField}>
        + Ajouter un champ
      </button>
    </section>

   {/if}

   {#if activeTab === 'epub'}
    <!-- EPUB Options -->
    <section class="config-section">
      <h3>Options EPUB</h3>

      <!-- EPUB Format selector -->
      <div class="form-group">
        <span class="label-text">Format EPUB</span>
        <div class="radio-group">
          <label class="radio-label">
            <input type="radio" bind:group={exportConfig.epubFormat} value="epub3" on:change={() => exportConfigChanged = true} />
            <span>EPUB 3</span>
          </label>
          <label class="radio-label">
            <input type="radio" bind:group={exportConfig.epubFormat} value="epub2" on:change={() => exportConfigChanged = true} />
            <span>EPUB 2</span>
          </label>
          <label class="radio-label">
            <input type="radio" bind:group={exportConfig.epubFormat} value="both" on:change={() => exportConfigChanged = true} />
            <span>Les deux</span>
          </label>
        </div>
      </div>

      <!-- Cover Image with preview and picker -->
      <div class="form-group">
        <span class="label-text">Image de couverture</span>

        <!-- Hidden file input for cover upload -->
        <input
          type="file"
          accept="image/*"
          bind:this={coverImageInputRef}
          on:change={handleCoverImageUpload}
          style="display: none;"
        />

        <div class="cover-image-section">
          {#if metadata.coverImage && coverImagePreview}
            <!-- Cover preview -->
            <div class="cover-preview">
              <img src={coverImagePreview} alt="Couverture" />
              <div class="cover-info">
                <span class="cover-filename">{metadata.coverImage}</span>
                <button
                  class="btn-icon btn-remove-cover"
                  on:click={removeCoverImage}
                  title="Retirer la couverture"
                >
                  ✕
                </button>
              </div>
            </div>
          {:else if metadata.coverImage}
            <!-- Cover path without preview (external reference) -->
            <div class="cover-path">
              <span class="cover-filename">{metadata.coverImage}</span>
              <button
                class="btn-icon btn-remove-cover"
                on:click={removeCoverImage}
                title="Retirer la couverture"
              >
                ✕
              </button>
            </div>
          {:else}
            <!-- No cover selected -->
            <div class="cover-empty">
              <span class="cover-empty-text">Aucune couverture selectionnee</span>
            </div>
          {/if}

          <div class="cover-actions">
            <button
              class="btn btn-secondary"
              on:click={() => coverImageInputRef?.click()}
              disabled={isUploadingCover}
            >
              {isUploadingCover ? '⏳' : '📤'} Importer
            </button>
            {#if $images.length > 0}
              <button
                class="btn btn-secondary"
                on:click={() => showCoverPicker = !showCoverPicker}
              >
                🖼️ Choisir
              </button>
            {/if}
          </div>

          <!-- Image picker dropdown -->
          {#if showCoverPicker}
            <div class="cover-picker">
              <div class="cover-picker-header">
                <span>Selectionner une image</span>
                <button class="btn-icon" on:click={() => showCoverPicker = false}>✕</button>
              </div>
              <div class="cover-picker-grid">
                {#each $images as image (image.id)}
                  <button
                    class="cover-picker-item"
                    class:selected={metadata.coverImage === image.filename}
                    on:click={() => selectCoverImage(image)}
                  >
                    <img src={image.dataUrl} alt={image.filename} />
                    <span class="cover-picker-filename">{image.filename}</span>
                  </button>
                {/each}
              </div>
            </div>
          {/if}
        </div>
      </div>

      <div class="form-group">
        <label for="coverNote">Note sur la couverture</label>
        <textarea
          id="coverNote"
          bind:value={metadata.coverNote}
          on:input={markChanged}
          placeholder="Ex: Redon Odilon, *Caliban* (detail),&#10;musee d'Orsay. Photo de..."
          rows="3"
        ></textarea>
      </div>

      <div class="form-group checkbox-group">
        <label class="checkbox-label">
          <input
            type="checkbox"
            bind:checked={metadata.navReorder}
            on:change={markChanged}
          />
          Reordonner la table des matieres dans le spine (apres la page de copyright ou de titre)
        </label>
      </div>

      <h4>Table des matieres</h4>

      <div class="form-group checkbox-group">
        <label class="checkbox-label">
          <input
            type="checkbox"
            checked={!metadata.toc}
            on:change={() => { metadata.toc = !metadata.toc; markChanged(); }}
          />
          Masquer la table des matieres
        </label>
      </div>

      {#if metadata.toc}
        <div class="form-group">
          <label for="tocDepth">Niveau de la table des matieres</label>
          <select
            id="tocDepth"
            bind:value={metadata.tocDepth}
            on:change={markChanged}
          >
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
            <option value={5}>5</option>
          </select>
        </div>

        <div class="form-group">
          <label for="tocTitle">Titre de la table des matieres</label>
          <input
            type="text"
            id="tocTitle"
            bind:value={metadata.tocTitle}
            on:input={markChanged}
            placeholder={metadata.lang?.startsWith('fr') ? 'Table des matieres' : 'Table of Contents'}
          />
        </div>
      {/if}

    </section>

    <!-- Accessibility -->
    <section class="config-section">
      <h3>Accessibilite (Ace by DAISY)</h3>

      <div class="form-group">
        <span class="label-text">Modes d'acces (accessModes)</span>
        <div class="checkbox-grid">
          {#each accessModeOptions as mode}
            <label class="checkbox-label">
              <input
                type="checkbox"
                checked={metadata.accessModes.includes(mode)}
                on:change={() => toggleAccessMode(mode)}
              />
              <span>{mode}</span>
            </label>
          {/each}
        </div>
      </div>

      <div class="form-group">
        <span class="label-text">Modes suffisants (accessModeSufficient)</span>
        <div class="checkbox-grid">
          {#each accessModeOptions as mode}
            <label class="checkbox-label">
              <input
                type="checkbox"
                checked={metadata.accessModeSufficient.includes(mode)}
                on:change={() => toggleAccessModeSufficient(mode)}
              />
              <span>{mode}</span>
            </label>
          {/each}
        </div>
      </div>

      <div class="form-group">
        <span class="label-text">Risques (accessibilityHazards)</span>
        <div class="checkbox-grid wide">
          {#each accessibilityHazardOptions as hazard}
            <label class="checkbox-label">
              <input
                type="checkbox"
                checked={metadata.accessibilityHazards.includes(hazard)}
                on:change={() => toggleAccessibilityHazard(hazard)}
              />
              <span>{hazard}</span>
            </label>
          {/each}
        </div>
      </div>

      <div class="form-group">
        <span class="label-text">Fonctionnalites (accessibilityFeatures)</span>
        <div class="checkbox-grid wide">
          {#each accessibilityFeatureOptions as feature}
            <label class="checkbox-label">
              <input
                type="checkbox"
                checked={metadata.accessibilityFeatures.includes(feature)}
                on:change={() => toggleAccessibilityFeature(feature)}
              />
              <span>{feature}</span>
            </label>
          {/each}
        </div>
      </div>

      <div class="form-group">
        <label for="accessibilitySummary">Resume d'accessibilite</label>
        <textarea
          id="accessibilitySummary"
          bind:value={metadata.accessibilitySummary}
          on:input={markChanged}
          placeholder="Ce livre est accessible."
          rows="2"
        ></textarea>
      </div>
    </section>

   {/if}

   {#if activeTab === 'indesign'}
    <!-- InDesign Sub-tabs -->
    <div class="indesign-subtabs">
      <button class="subtab" class:active={indesignSubTab === 'mapping'} on:click={() => indesignSubTab = 'mapping'}>Mapping</button>
      <button class="subtab" class:active={indesignSubTab === 'typography'} on:click={() => indesignSubTab = 'typography'}>Typographie</button>
      <button class="subtab" class:active={indesignSubTab === 'pagelayout'} on:click={() => indesignSubTab = 'pagelayout'}>Mise en page</button>
      <button class="subtab" class:active={indesignSubTab === 'grep'} on:click={() => indesignSubTab = 'grep'}>Grep</button>
      <button class="subtab" class:active={indesignSubTab === 'export'} on:click={() => indesignSubTab = 'export'}>Export</button>
      <button class="subtab" class:active={indesignSubTab === 'templates'} on:click={() => indesignSubTab = 'templates'}>Templates</button>
    </div>

    <!-- ======== MAPPING SUB-TAB ======== -->
    {#if indesignSubTab === 'mapping'}
    <section class="config-section">
      <h3>Styles de paragraphe</h3>
      <p class="section-description">Correspondance entre les styles Markdown et les styles InDesign</p>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="id-h1">H1 (Titre 1)</label>
          <input id="id-h1" type="text" bind:value={indesignMapping.h1} on:input={() => indesignMappingChanged = true} placeholder="H1" />
        </div>
        <div class="form-group flex-1">
          <label for="id-h2">H2 (Titre 2)</label>
          <input id="id-h2" type="text" bind:value={indesignMapping.h2} on:input={() => indesignMappingChanged = true} placeholder="H2" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="id-h3">H3 (Titre 3)</label>
          <input id="id-h3" type="text" bind:value={indesignMapping.h3} on:input={() => indesignMappingChanged = true} placeholder="H3" />
        </div>
        <div class="form-group flex-1">
          <label for="id-h4">H4 (Titre 4)</label>
          <input id="id-h4" type="text" bind:value={indesignMapping.h4} on:input={() => indesignMappingChanged = true} placeholder="H4" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="id-h5">H5 (Titre 5)</label>
          <input id="id-h5" type="text" bind:value={indesignMapping.h5} on:input={() => indesignMappingChanged = true} placeholder="H5" />
        </div>
        <div class="form-group flex-1">
          <label for="id-h6">H6 (Titre 6)</label>
          <input id="id-h6" type="text" bind:value={indesignMapping.h6} on:input={() => indesignMappingChanged = true} placeholder="H6" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="id-normal">Paragraphe normal</label>
          <input id="id-normal" type="text" bind:value={indesignMapping.normal} on:input={() => indesignMappingChanged = true} placeholder="Body text" />
        </div>
        <div class="form-group flex-1">
          <label for="id-quote">Citation (blockquote)</label>
          <input id="id-quote" type="text" bind:value={indesignMapping.quote} on:input={() => indesignMappingChanged = true} placeholder="Citation" />
        </div>
      </div>

      <div class="form-group">
        <label for="id-bulletlist">Liste a puces</label>
        <input id="id-bulletlist" type="text" bind:value={indesignMapping.bulletlist} on:input={() => indesignMappingChanged = true} placeholder="Bulleted list" />
      </div>
    </section>

    <section class="config-section">
      <h3>Styles de caractere</h3>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="id-italic">Italique</label>
          <input id="id-italic" type="text" bind:value={indesignMapping.italic} on:input={() => indesignMappingChanged = true} placeholder="Italic" />
        </div>
        <div class="form-group flex-1">
          <label for="id-bold">Gras</label>
          <input id="id-bold" type="text" bind:value={indesignMapping.bold} on:input={() => indesignMappingChanged = true} placeholder="Bold" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="id-bolditalic">Gras italique</label>
          <input id="id-bolditalic" type="text" bind:value={indesignMapping.bolditalic} on:input={() => indesignMappingChanged = true} placeholder="Bold italic" />
        </div>
        <div class="form-group flex-1">
          <label for="id-underline">Souligne</label>
          <input id="id-underline" type="text" bind:value={indesignMapping.underline} on:input={() => indesignMappingChanged = true} placeholder="Underline" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="id-smallcaps">Petites capitales</label>
          <input id="id-smallcaps" type="text" bind:value={indesignMapping.smallcaps} on:input={() => indesignMappingChanged = true} placeholder="Small caps" />
        </div>
        <div class="form-group flex-1">
          <label for="id-strikethrough">Barre</label>
          <input id="id-strikethrough" type="text" bind:value={indesignMapping.strikethrough} on:input={() => indesignMappingChanged = true} placeholder="Strikethrough" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group flex-1">
          <label for="id-subscript">Indice</label>
          <input id="id-subscript" type="text" bind:value={indesignMapping.subscript} on:input={() => indesignMappingChanged = true} placeholder="Subscript" />
        </div>
        <div class="form-group flex-1">
          <label for="id-superscript">Exposant</label>
          <input id="id-superscript" type="text" bind:value={indesignMapping.superscript} on:input={() => indesignMappingChanged = true} placeholder="Superscript" />
        </div>
      </div>

      <div class="form-group">
        <label for="id-note">Note de bas de page</label>
        <input id="id-note" type="text" bind:value={indesignMapping.note} on:input={() => indesignMappingChanged = true} placeholder="Footnote" />
      </div>
    </section>

    <section class="config-section">
      <h3>Options</h3>
      <div class="form-group">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={indesignMapping.removeBlankPages} on:change={() => indesignMappingChanged = true} />
          <span>Supprimer les pages blanches</span>
        </label>
      </div>
    </section>
    {/if}

    <!-- ======== TYPOGRAPHY SUB-TAB ======== -->
    {#if indesignSubTab === 'typography'}

    <!-- Language Profile -->
    <section class="config-section">
      <h3>Profil linguistique</h3>
      <div class="form-group">
        <label for="typo-profile">Langue</label>
        <select id="typo-profile" bind:value={typographyConfig.languageProfile} on:change={() => typographyConfigChanged = true}>
          {#each languageProfiles as profile}
            <option value={profile.value}>{profile.label}</option>
          {/each}
        </select>
      </div>
    </section>

    <!-- Corrections -->
    <section class="config-section">
      <h3>Corrections</h3>
      <div class="checkbox-grid wide">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.moveNotes} on:change={() => typographyConfigChanged = true} />
          <span>D\u00e9placer les notes avant la ponctuation</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.convertEllipsis} on:change={() => typographyConfigChanged = true} />
          <span>Convertir ... en points de suspension</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.replaceApostrophes} on:change={() => typographyConfigChanged = true} />
          <span>Remplacer les apostrophes droites</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.replaceDashes} on:change={() => typographyConfigChanged = true} />
          <span>Remplacer les tirets cadratins par des demi-cadratins</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.fixIsolatedHyphens} on:change={() => typographyConfigChanged = true} />
          <span>Corriger les tirets isol\u00e9s</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.fixValueRanges} on:change={() => typographyConfigChanged = true} />
          <span>Corriger les tirets dans les intervalles</span>
        </label>
      </div>
    </section>

    <!-- Spaces & Returns -->
    <section class="config-section">
      <h3>Espaces et retours</h3>
      <div class="checkbox-grid wide">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.fixDoubleSpaces} on:change={() => typographyConfigChanged = true} />
          <span>Supprimer les doubles espaces</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.removeDoubleReturns} on:change={() => typographyConfigChanged = true} />
          <span>Supprimer les doubles retours</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.removeSpacesBeforePunctuation} on:change={() => typographyConfigChanged = true} />
          <span>Supprimer les espaces avant la ponctuation</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.removeSpacesStartParagraph} on:change={() => typographyConfigChanged = true} />
          <span>Supprimer les espaces en d\u00e9but de paragraphe</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.removeSpacesEndParagraph} on:change={() => typographyConfigChanged = true} />
          <span>Supprimer les espaces en fin de paragraphe</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.removeTabs} on:change={() => typographyConfigChanged = true} />
          <span>Supprimer les tabulations</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.formatEspaces} on:change={() => typographyConfigChanged = true} />
          <span>Espaces ins\u00e9cables apr\u00e8s les abr\u00e9viations</span>
        </label>
      </div>

      <!-- Incise dashes with type dropdown -->
      <div class="typo-compound-row">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.fixDashIncises} on:change={() => typographyConfigChanged = true} />
          <span>Espaces autour des incises</span>
        </label>
        {#if typographyConfig.corrections.fixDashIncises}
          <select class="typo-inline-select" bind:value={typographyConfig.corrections.fixDashIncisesType} on:change={() => typographyConfigChanged = true}>
            <option value={0}>Espace fine</option>
            <option value={1}>Espace ins\u00e9cable</option>
          </select>
        {/if}
      </div>

      <!-- Typographic spaces with sub-options -->
      <div class="typo-compound-row">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.fixTypoSpaces} on:change={() => typographyConfigChanged = true} />
          <span>Espaces typographiques avant la ponctuation</span>
        </label>
      </div>
      {#if typographyConfig.corrections.fixTypoSpaces}
        <div class="typo-sub-options">
          <div class="typo-sub-row">
            <span class="typo-sub-label">Avant ; ? ! \u00ab \u00bb</span>
            <select class="typo-inline-select" bind:value={typographyConfig.corrections.fixTypoSpacesPunct} on:change={() => typographyConfigChanged = true}>
              <option value={0}>Espace fine</option>
              <option value={1}>Espace ins\u00e9cable</option>
            </select>
          </div>
          <div class="typo-sub-row">
            <span class="typo-sub-label">Avant :</span>
            <select class="typo-inline-select" bind:value={typographyConfig.corrections.fixTypoSpacesColon} on:change={() => typographyConfigChanged = true}>
              <option value={0}>Espace fine</option>
              <option value={1}>Espace ins\u00e9cable</option>
            </select>
          </div>
        </div>
      {/if}
    </section>

    <!-- Style Definition -->
    <section class="config-section">
      <h3>D\u00e9finition des styles</h3>
      <div class="form-row">
        <div class="form-group flex-1">
          <label for="typo-note">Note</label>
          <input id="typo-note" type="text" bind:value={typographyConfig.styles.noteStyle} on:input={() => typographyConfigChanged = true} />
        </div>
        <div class="form-group flex-1">
          <label for="typo-italic">Italique</label>
          <input id="typo-italic" type="text" bind:value={typographyConfig.styles.italicStyle} on:input={() => typographyConfigChanged = true} />
        </div>
      </div>
      <div class="form-row">
        <div class="form-group flex-1">
          <label for="typo-smallcaps">Petites capitales</label>
          <input id="typo-smallcaps" type="text" bind:value={typographyConfig.styles.smallCapsStyle} on:input={() => typographyConfigChanged = true} />
        </div>
        <div class="form-group flex-1">
          <label for="typo-capitals">Capitales</label>
          <input id="typo-capitals" type="text" bind:value={typographyConfig.styles.capitalsStyle} on:input={() => typographyConfigChanged = true} />
        </div>
      </div>
      <div class="form-group">
        <label for="typo-superscript">Exposant</label>
        <input id="typo-superscript" type="text" bind:value={typographyConfig.styles.superscriptStyle} on:input={() => typographyConfigChanged = true} />
      </div>
    </section>

    <!-- Style Application -->
    <section class="config-section">
      <h3>Application des styles</h3>
      <div class="checkbox-grid wide">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.applyNoteStyle} on:change={() => typographyConfigChanged = true} />
          <span>Appliquer le style aux appels de note</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.applyItalicStyle} on:change={() => typographyConfigChanged = true} />
          <span>Appliquer le style au texte en italique</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.applyItalicExpressions} on:change={() => typographyConfigChanged = true} />
          <span>Appliquer l\u2019italique aux expressions linguistiques</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.corrections.applyExposantStyle} on:change={() => typographyConfigChanged = true} />
          <span>Appliquer le style au texte en exposant</span>
        </label>
      </div>
    </section>

    <!-- Formatting -->
    <section class="config-section">
      <h3>Formatage</h3>
      <div class="checkbox-grid wide">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.formatting.formatSiecles} on:change={() => typographyConfigChanged = true} />
          <span>Formater les si\u00e8cles (XIVe)</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.formatting.formatOrdinaux} on:change={() => typographyConfigChanged = true} />
          <span>Formater les ordinaux (IIe)</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.formatting.formatReferences} on:change={() => typographyConfigChanged = true} />
          <span>Formater les r\u00e9f\u00e9rences (Tome III)</span>
        </label>
      </div>

      <!-- Numbers with sub-options -->
      <div class="typo-compound-row">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={typographyConfig.formatting.formatNumbers} on:change={() => typographyConfigChanged = true} />
          <span>Formater les nombres</span>
        </label>
      </div>
      {#if typographyConfig.formatting.formatNumbers}
        <div class="typo-sub-options">
          <div class="typo-compound-row">
            <label class="checkbox-label">
              <input type="checkbox" bind:checked={typographyConfig.formatting.addThousandsSep} on:change={() => typographyConfigChanged = true} />
              <span>Ajouter un s\u00e9parateur de milliers</span>
            </label>
            {#if typographyConfig.formatting.addThousandsSep}
              <select class="typo-inline-select" bind:value={typographyConfig.formatting.thousandsSepType} on:change={() => typographyConfigChanged = true}>
                <option value={0}>Espace fine</option>
                <option value={1}>Virgule</option>
                <option value={2}>Point</option>
              </select>
            {/if}
          </div>
          <label class="checkbox-label">
            <input type="checkbox" bind:checked={typographyConfig.formatting.excludeYears} on:change={() => typographyConfigChanged = true} />
            <span>Exclure les ann\u00e9es (0\u20132050)</span>
          </label>
          <label class="checkbox-label">
            <input type="checkbox" bind:checked={typographyConfig.formatting.useComma} on:change={() => typographyConfigChanged = true} />
            <span>Utiliser la virgule comme s\u00e9parateur d\u00e9cimal</span>
          </label>
        </div>
      {/if}
    </section>

    {/if}

    <!-- ======== PAGELAYOUT SUB-TAB ======== -->
    {#if indesignSubTab === 'pagelayout'}
    <section class="config-section">
      <h3>Styles conditionnels</h3>

      <div class="form-group">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={pagelayoutConfig.layout.enableStyleAfter} on:change={() => pagelayoutConfigChanged = true} />
          <span>Activer le style conditionnel (style after trigger)</span>
        </label>
      </div>

      {#if pagelayoutConfig.layout.enableStyleAfter}
        <div class="form-group">
          <label>Styles declencheurs</label>
          <div class="chip-container">
            {#each pagelayoutConfig.layout.triggerStyles as style}
              <span class="chip">
                {style}
                <button class="chip-remove" on:click={() => removePagelayoutTriggerStyle(style)}>x</button>
              </span>
            {/each}
          </div>
          <div class="chip-input-row">
            <input
              type="text"
              bind:value={pagelayoutTriggerInput}
              on:keydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPagelayoutTriggerStyle(); } }}
              placeholder="Ajouter un style et appuyer sur Entree"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="pl-target">Style cible</label>
          <input id="pl-target" type="text" bind:value={pagelayoutConfig.layout.targetStyle} on:input={() => pagelayoutConfigChanged = true} />
        </div>
      {/if}
    </section>

    <section class="config-section">
      <h3>Gabarit</h3>

      <div class="form-group">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={pagelayoutConfig.layout.applyMasterToLastPage} on:change={() => pagelayoutConfigChanged = true} />
          <span>Appliquer un gabarit a la derniere page</span>
        </label>
      </div>

      {#if pagelayoutConfig.layout.applyMasterToLastPage}
        <div class="form-group">
          <label for="pl-master">Nom du gabarit</label>
          <input id="pl-master" type="text" bind:value={pagelayoutConfig.layout.masterName} on:input={() => pagelayoutConfigChanged = true} placeholder="Ex: B-Master" />
        </div>
      {/if}
    </section>
    {/if}

    <!-- ======== GREP SUB-TAB ======== -->
    {#if indesignSubTab === 'grep'}
    <section class="config-section">
      <h3>Regles GREP</h3>
      <p class="section-description">Rechercher/remplacer avec des expressions regulieres InDesign</p>

      {#each grepConfig.rules as rule, index}
        <div class="grep-rule">
          <div class="grep-rule-header">
            <label class="checkbox-label">
              <input type="checkbox" bind:checked={rule.enabled} on:change={() => grepConfigChanged = true} />
              <span>Regle {index + 1}</span>
            </label>
            <button class="btn-icon btn-remove" on:click={() => removeGrepRule(index)} title="Supprimer">x</button>
          </div>

          <div class="form-group">
            <label>Rechercher (Find)</label>
            <input type="text" class="grep-input" bind:value={rule.find} on:input={() => grepConfigChanged = true} placeholder="Pattern GREP" />
          </div>
          <div class="form-row">
            <div class="form-group flex-1">
              <label>Style para. (filtre)</label>
              <input type="text" bind:value={rule.findParaStyle} on:input={() => grepConfigChanged = true} placeholder="(aucun)" />
            </div>
            <div class="form-group flex-1">
              <label>Style car. (filtre)</label>
              <input type="text" bind:value={rule.findCharStyle} on:input={() => grepConfigChanged = true} placeholder="(aucun)" />
            </div>
          </div>

          <div class="form-group">
            <label>Remplacer (Change)</label>
            <input type="text" class="grep-input" bind:value={rule.change} on:input={() => grepConfigChanged = true} placeholder="Remplacement" />
          </div>
          <div class="form-row">
            <div class="form-group flex-1">
              <label>Style para. (appliquer)</label>
              <input type="text" bind:value={rule.changeParaStyle} on:input={() => grepConfigChanged = true} placeholder="(aucun)" />
            </div>
            <div class="form-group flex-1">
              <label>Style car. (appliquer)</label>
              <input type="text" bind:value={rule.changeCharStyle} on:input={() => grepConfigChanged = true} placeholder="(aucun)" />
            </div>
          </div>
        </div>
      {/each}

      <button class="btn btn-secondary btn-add" on:click={addGrepRule}>
        + Ajouter une regle
      </button>
    </section>
    {/if}

    <!-- ======== EXPORT SUB-TAB ======== -->
    {#if indesignSubTab === 'export'}
    <section class="config-section">
      <h3>Format PDF</h3>

      <div class="form-group">
        <label class="radio-label">
          <input type="radio" bind:group={exportConfig.pdfFormat} value="interactive" on:change={() => exportConfigChanged = true} />
          <span>PDF interactif</span>
        </label>
        <label class="radio-label">
          <input type="radio" bind:group={exportConfig.pdfFormat} value="print" on:change={() => exportConfigChanged = true} />
          <span>PDF impression</span>
        </label>
        <label class="radio-label">
          <input type="radio" bind:group={exportConfig.pdfFormat} value="both" on:change={() => exportConfigChanged = true} />
          <span>Interactif + Impression (les deux)</span>
        </label>
      </div>

      {#if exportConfig.pdfFormat === 'print' || exportConfig.pdfFormat === 'both'}
        <div class="form-group">
          <label for="pdf-preset">Preset PDF impression</label>
          <input id="pdf-preset" type="text" bind:value={exportConfig.pdfPreset} on:input={() => exportConfigChanged = true} placeholder="Ex: [Press Quality]" />
        </div>
      {/if}
    </section>

    <section class="config-section">
      <h3>Format EPUB</h3>

      <div class="form-group">
        <label class="radio-label">
          <input type="radio" bind:group={exportConfig.epubFormat} value="epub3" on:change={() => exportConfigChanged = true} />
          <span>EPUB 3</span>
        </label>
        <label class="radio-label">
          <input type="radio" bind:group={exportConfig.epubFormat} value="epub2" on:change={() => exportConfigChanged = true} />
          <span>EPUB 2</span>
        </label>
        <label class="radio-label">
          <input type="radio" bind:group={exportConfig.epubFormat} value="both" on:change={() => exportConfigChanged = true} />
          <span>Les deux</span>
        </label>
      </div>
    </section>

    <section class="config-section">
      <h3>Activation</h3>

      <div class="form-group">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={exportConfig.indesignEnabled} on:change={() => exportConfigChanged = true} />
          <span>Generer InDesign</span>
        </label>
      </div>
      <div class="form-group">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={exportConfig.pdfEnabled} on:change={() => exportConfigChanged = true} />
          <span>Generer PDF</span>
        </label>
      </div>
      <div class="form-group">
        <label class="checkbox-label">
          <input type="checkbox" bind:checked={exportConfig.epubEnabled} on:change={() => exportConfigChanged = true} />
          <span>Generer EPUB</span>
        </label>
      </div>
    </section>
    {/if}

    <!-- ======== TEMPLATES SUB-TAB ======== -->
    {#if indesignSubTab === 'templates'}
    <section class="config-section">
      <h3>Templates InDesign (.indd)</h3>
      <p class="section-description">Templates InDesign utilises par BookCreator pour generer les documents du livre. Importez vos fichiers .indd (titlepage, bodymatter, backmatter, etc.).</p>

      <input
        type="file"
        accept=".indd"
        multiple
        bind:this={indesignTemplateInputRef}
        on:change={handleIndesignTemplateUpload}
        style="display: none;"
      />

      {#if indesignTemplateFiles.length > 0}
        <div class="template-list">
          {#each indesignTemplateFiles as tpl (tpl.id)}
            <div class="template-item">
              <span class="template-filename">{tpl.filename}</span>
              <span class="template-size">{(tpl.size / 1024).toFixed(0)} Ko</span>
              <button
                class="btn-icon btn-remove"
                on:click={() => deleteIndesignTemplate(tpl.id)}
                title="Supprimer"
              >
                ✕
              </button>
            </div>
          {/each}
        </div>
      {:else}
        <p class="empty-state">Aucun template InDesign importe</p>
      {/if}

      <button
        class="btn btn-secondary"
        on:click={() => indesignTemplateInputRef?.click()}
        disabled={isUploadingIndesignTemplate}
      >
        {isUploadingIndesignTemplate ? 'Import en cours...' : 'Importer des templates .indd'}
      </button>

      <div class="template-help">
        <p class="help-text">Templates reconnus par BookCreator :</p>
        <ul class="help-list">
          <li><strong>titlepage.indd</strong> — page de titre (standalone, premier)</li>
          <li><strong>frontmatter.indd</strong> — template par defaut pour le frontmatter</li>
          <li><strong>bodymatter.indd</strong> — template par defaut pour les chapitres</li>
          <li><strong>backmatter.indd</strong> — template par defaut pour le backmatter</li>
          <li><strong>bibliography.indd</strong> — specialise pour la bibliographie</li>
          <li><strong>toc.indd</strong> — specialise pour la table des matieres</li>
          <li><strong>endpage.indd</strong> — page de fin (standalone, dernier)</li>
          <li><strong>cover.indd</strong> — couverture (hors du book)</li>
        </ul>
      </div>
    </section>
    {/if}

   {/if}
  </div>

  <!-- Hidden file input for YAML import -->
  <input
    type="file"
    accept=".yaml,.yml"
    bind:this={fileInputRef}
    on:change={handleFileImport}
    style="display: none;"
  />

  <!-- Hidden file input for InDesign mapping import -->
  <input
    type="file"
    accept=".mdconfig,.json"
    bind:this={indesignFileInputRef}
    on:change={handleIndesignFileImport}
    style="display: none;"
  />

  <!-- Hidden file input for generic InDesign config import (typography, pagelayout, grep, export) -->
  <input
    type="file"
    accept=".json"
    bind:this={configFileInputRef}
    on:change={handleConfigFileImport}
    style="display: none;"
  />

  <div class="config-footer">
    <div class="footer-left">
      {#if activeTab === 'general'}
        <button class="btn btn-secondary btn-import-export" on:click={() => handleConfigImportClick('general')} title="Importer general.json">
          Importer
        </button>
        <button class="btn btn-secondary btn-import-export" on:click={() => exportConfigFile('general')} title="Exporter general.json">
          Exporter
        </button>
      {:else if activeTab === 'indesign' && indesignSubTab === 'mapping'}
        <button class="btn btn-secondary btn-import-export" on:click={handleIndesignImportClick} title="Importer mapping.json">
          Importer
        </button>
        <button class="btn btn-secondary btn-import-export" on:click={exportIndesignMapping} title="Exporter mapping.json">
          Exporter
        </button>
      {:else if activeTab === 'indesign' && indesignSubTab === 'typography'}
        <button class="btn btn-secondary btn-import-export" on:click={() => handleConfigImportClick('typography')} title="Importer typography.json">
          Importer
        </button>
        <button class="btn btn-secondary btn-import-export" on:click={() => exportConfigFile('typography')} title="Exporter typography.json">
          Exporter
        </button>
      {:else if activeTab === 'indesign' && indesignSubTab === 'pagelayout'}
        <button class="btn btn-secondary btn-import-export" on:click={() => handleConfigImportClick('pagelayout')} title="Importer pagelayout.json">
          Importer
        </button>
        <button class="btn btn-secondary btn-import-export" on:click={() => exportConfigFile('pagelayout')} title="Exporter pagelayout.json">
          Exporter
        </button>
      {:else if activeTab === 'indesign' && indesignSubTab === 'grep'}
        <button class="btn btn-secondary btn-import-export" on:click={() => handleConfigImportClick('grep')} title="Importer grep.json">
          Importer
        </button>
        <button class="btn btn-secondary btn-import-export" on:click={() => exportConfigFile('grep')} title="Exporter grep.json">
          Exporter
        </button>
      {:else if activeTab === 'indesign' && indesignSubTab === 'export'}
        <button class="btn btn-secondary btn-import-export" on:click={() => handleConfigImportClick('export')} title="Importer export.json">
          Importer
        </button>
        <button class="btn btn-secondary btn-import-export" on:click={() => exportConfigFile('export')} title="Exporter export.json">
          Exporter
        </button>
      {:else if activeTab !== 'indesign' && !$presetMode}
        <button class="btn btn-secondary btn-import-export" on:click={handleImportClick} title="Importer un fichier YAML">
          Importer
        </button>
        <button class="btn btn-secondary btn-import-export" on:click={handleExport} title="Exporter en YAML">
          Exporter
        </button>
      {/if}
    </div>
    <div class="footer-right">
      <button class="btn btn-secondary" on:click={handleClose}>
        Annuler
      </button>
      {#if activeTab === 'general'}
        <button class="btn btn-primary" on:click={saveGeneralConfig} disabled={isSavingGeneral || !generalConfigChanged}>
          {isSavingGeneral ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
      {:else if activeTab === 'indesign'}
        {#if indesignSubTab === 'mapping'}
          <button class="btn btn-primary" on:click={saveIndesignMapping} disabled={isSavingIndesign || !indesignMappingChanged}>
            {isSavingIndesign ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        {:else if indesignSubTab === 'typography'}
          <button class="btn btn-primary" on:click={saveTypographyConfig} disabled={isSavingTypography || !typographyConfigChanged}>
            {isSavingTypography ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        {:else if indesignSubTab === 'pagelayout'}
          <button class="btn btn-primary" on:click={savePagelayoutConfig} disabled={isSavingPagelayout || !pagelayoutConfigChanged}>
            {isSavingPagelayout ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        {:else if indesignSubTab === 'grep'}
          <button class="btn btn-primary" on:click={saveGrepConfig} disabled={isSavingGrep || !grepConfigChanged}>
            {isSavingGrep ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        {:else if indesignSubTab === 'export'}
          <button class="btn btn-primary" on:click={saveExportConfig} disabled={isSavingExport || !exportConfigChanged}>
            {isSavingExport ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        {/if}
      {:else if !$presetMode}
        <button class="btn btn-primary" on:click={saveMetadata} disabled={isSaving || !hasChanges}>
          {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
      {/if}
    </div>
  </div>
</div>

<style>
  .config-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--panel-background);
  }

  .config-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
  }

  .config-header h2 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .btn-close {
    background: transparent;
    border: none;
    font-size: 1.25rem;
    cursor: pointer;
    color: var(--text-muted);
    padding: 0.25rem;
    border-radius: 4px;
    transition: all 0.2s;
  }

  .btn-close:hover {
    background: var(--hover-background);
    color: var(--error-color);
  }

  .config-tabs {
    display: flex;
    border-bottom: 1px solid var(--border-color);
    background: var(--panel-background);
    padding: 0 1rem;
    gap: 0;
  }

  .config-tab {
    padding: 0.6rem 1.25rem;
    border: none;
    background: transparent;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-muted);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .config-tab:hover {
    color: var(--text-primary);
    background: var(--hover-background);
  }

  .config-tab.active {
    color: var(--accent-color);
    border-bottom-color: var(--accent-color);
  }

  .config-content {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  .config-section {
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--border-color);
  }

  .config-section:last-child {
    border-bottom: none;
  }

  .config-section h3 {
    margin: 0 0 1rem 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .form-group {
    margin-bottom: 0.75rem;
  }

  .form-group label,
  .form-group .label-text {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .form-group input[type="text"],
  .form-group input[type="date"],
  .form-group select,
  .form-group textarea {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.875rem;
    background: var(--input-background, var(--panel-background));
    color: var(--text-primary);
    font-family: inherit;
  }

  .form-group input:focus,
  .form-group select:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  .form-group textarea {
    resize: vertical;
    min-height: 60px;
  }

  .form-hint {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.7rem;
    color: var(--text-muted);
    font-style: italic;
  }

  .preset-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.75rem;
    background: var(--accent-color-light, #dbeafe);
    border: 1px solid var(--accent-color);
    border-radius: 4px;
    font-size: 0.85rem;
    color: var(--text-primary);
  }

  .preset-name {
    font-weight: 500;
  }

  .form-row {
    display: flex;
    gap: 0.75rem;
  }

  .flex-1 {
    flex: 1;
  }

  /* Row styles */
  .creator-row,
  .identifier-row,
  .font-row,
  .subject-row,
  .custom-field-row {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    align-items: center;
  }

  .creator-name,
  .identifier-text,
  .subject-text {
    flex: 2;
  }

  .creator-role,
  .identifier-scheme {
    flex: 1;
    min-width: 100px;
  }

  .font-row input {
    flex: 1;
  }

  .subject-block {
    margin-bottom: 0.75rem;
    padding: 0.5rem;
    background: var(--hover-background);
    border-radius: 4px;
  }

  .subject-details {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .subject-authority {
    width: 100px;
  }

  .subject-term {
    flex: 1;
  }

  .custom-key {
    flex: 1;
  }

  .custom-value {
    flex: 2;
  }

  /* Buttons */
  .btn {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-primary {
    background: var(--accent-color);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
  }

  .btn-secondary:hover {
    background: var(--hover-background);
  }

  .btn-add {
    margin-top: 0.5rem;
    font-size: 0.8rem;
    padding: 0.35rem 0.75rem;
  }

  .btn-icon {
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    padding: 0.4rem 0.6rem;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.2s;
    flex-shrink: 0;
  }

  .btn-icon:hover:not(:disabled) {
    background: var(--hover-background);
  }

  .btn-icon:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .btn-remove:hover:not(:disabled) {
    color: var(--error-color);
    border-color: var(--error-color);
  }

  /* Checkbox styling */
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-size: 0.8rem;
    color: var(--text-primary);
  }

  .checkbox-label input[type="checkbox"] {
    width: auto;
    cursor: pointer;
  }

  .checkbox-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 0.5rem;
  }

  .checkbox-grid.wide {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }

  /* Typography compound controls */
  .typo-compound-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .typo-inline-select {
    padding: 0.2rem 0.4rem;
    font-size: 0.75rem;
    background: var(--bg-input, #333);
    color: var(--text-primary);
    border: 1px solid var(--border-color, #555);
    border-radius: 4px;
    min-width: 120px;
  }

  .typo-sub-options {
    margin-left: 1.5rem;
    padding-left: 0.75rem;
    border-left: 2px solid var(--border-color, #444);
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
  }

  .typo-sub-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .typo-sub-label {
    font-size: 0.75rem;
    color: var(--text-secondary, #999);
    min-width: 110px;
  }

  /* Footer */
  .config-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    border-top: 1px solid var(--border-color);
    background: var(--panel-background);
  }

  .footer-left,
  .footer-right {
    display: flex;
    gap: 0.5rem;
  }

  .btn-import-export {
    font-size: 0.8rem;
    padding: 0.4rem 0.75rem;
  }

  /* Cover image section */
  .cover-image-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .cover-preview {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    padding: 0.75rem;
    background: var(--hover-background);
    border-radius: 6px;
    border: 1px solid var(--border-color);
  }

  .cover-preview img {
    max-width: 80px;
    max-height: 120px;
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .cover-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
  }

  .cover-filename {
    font-size: 0.8rem;
    color: var(--text-primary);
    word-break: break-all;
  }

  .cover-path {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: var(--hover-background);
    border-radius: 4px;
    border: 1px solid var(--border-color);
  }

  .cover-empty {
    padding: 1rem;
    text-align: center;
    background: var(--hover-background);
    border-radius: 4px;
    border: 1px dashed var(--border-color);
  }

  .cover-empty-text {
    font-size: 0.8rem;
    color: var(--text-muted);
  }

  .cover-actions {
    display: flex;
    gap: 0.5rem;
  }

  .cover-actions .btn {
    font-size: 0.8rem;
    padding: 0.4rem 0.75rem;
  }

  .btn-remove-cover {
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    font-size: 0.8rem;
    color: var(--text-muted);
    transition: all 0.2s;
  }

  .btn-remove-cover:hover {
    color: var(--error-color);
    border-color: var(--error-color);
  }

  /* Cover image picker */
  .cover-picker {
    background: var(--panel-background);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }

  .cover-picker-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0.75rem;
    background: var(--hover-background);
    border-bottom: 1px solid var(--border-color);
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-secondary);
  }

  .cover-picker-header .btn-icon {
    padding: 0.2rem 0.4rem;
    font-size: 0.75rem;
  }

  .cover-picker-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: 0.5rem;
    padding: 0.75rem;
    max-height: 200px;
    overflow-y: auto;
  }

  .cover-picker-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem;
    background: transparent;
    border: 2px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .cover-picker-item:hover {
    background: var(--hover-background);
    border-color: var(--border-color);
  }

  .cover-picker-item.selected {
    border-color: var(--accent-color);
    background: var(--hover-background);
  }

  .cover-picker-item img {
    max-width: 60px;
    max-height: 80px;
    object-fit: contain;
    border-radius: 3px;
  }

  .cover-picker-filename {
    font-size: 0.65rem;
    color: var(--text-muted);
    text-align: center;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Custom language picker row */
  .custom-lang-row {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .custom-lang-row input {
    flex: 0 0 auto;
  }

  /* BISAC picker */
  .bisac-picker {
    position: relative;
    margin-top: 0.35rem;
  }

  .bisac-search {
    width: 100%;
    padding: 0.4rem 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.8rem;
    background: var(--input-background, var(--panel-background));
    color: var(--text-primary);
  }

  .bisac-search:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  .bisac-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 200px;
    overflow-y: auto;
    background: var(--panel-background, #1a1a1a);
    border: 1px solid var(--border-color);
    border-radius: 0 0 4px 4px;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .bisac-option {
    display: flex;
    gap: 0.5rem;
    padding: 0.35rem 0.5rem;
    cursor: pointer;
    font-size: 0.75rem;
    align-items: baseline;
    border-bottom: 1px solid var(--border-color);
  }

  .bisac-option:hover {
    background: var(--hover-background);
  }

  .bisac-code {
    font-family: 'SF Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
    font-size: 0.7rem;
    color: var(--text-muted);
    flex-shrink: 0;
  }

  .bisac-desc {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-primary);
  }

  .bisac-no-results {
    padding: 0.5rem;
    font-size: 0.75rem;
    color: var(--text-muted);
    text-align: center;
  }

  .bisac-pill {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: var(--hover-background);
    cursor: pointer;
    font-size: 0.8rem;
  }

  .bisac-pill:hover {
    border-color: var(--accent-color);
  }

  .bisac-pill .bisac-code {
    flex-shrink: 0;
  }

  .bisac-pill .bisac-desc {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* InDesign sub-tabs */
  .indesign-subtabs {
    display: flex;
    gap: 0;
    border-bottom: 1px solid var(--border-color);
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }

  .subtab {
    padding: 0.4rem 0.75rem;
    border: none;
    background: transparent;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--text-muted);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .subtab:hover {
    color: var(--text-primary);
    background: var(--hover-background);
  }

  .subtab.active {
    color: var(--accent-color);
    border-bottom-color: var(--accent-color);
  }

  /* Section description */
  .section-description {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin: -0.5rem 0 0.75rem 0;
  }

  /* Chip/Tag UI */
  .chip-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-bottom: 0.5rem;
    min-height: 1.5rem;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.2rem 0.5rem;
    background: var(--hover-background);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    font-size: 0.75rem;
    color: var(--text-primary);
  }

  .chip-remove {
    background: transparent;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 0.7rem;
    padding: 0 0.15rem;
    line-height: 1;
    border-radius: 50%;
    transition: color 0.2s;
  }

  .chip-remove:hover {
    color: var(--error-color);
  }

  .chip-input-row {
    display: flex;
    gap: 0.5rem;
  }

  .chip-input-row input {
    flex: 1;
    padding: 0.35rem 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.8rem;
    background: var(--input-background, var(--panel-background));
    color: var(--text-primary);
  }

  .chip-input-row input:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  /* Grep rule styling */
  .grep-rule {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
    background: var(--hover-background);
    border: 1px solid var(--border-color);
    border-radius: 6px;
  }

  .grep-rule-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .grep-input {
    font-family: 'SF Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
    font-size: 0.8rem;
  }

  /* Radio button styling */
  .radio-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-size: 0.8rem;
    color: var(--text-primary);
    margin-bottom: 0.4rem;
  }

  .radio-label input[type="radio"] {
    width: auto;
    cursor: pointer;
  }

  /* InDesign template list */
  .template-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
  }

  .template-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.5rem;
    background: var(--hover-background);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.8rem;
  }

  .template-filename {
    flex: 1;
    color: var(--text-primary);
    font-family: 'SF Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
    font-size: 0.75rem;
  }

  .template-size {
    color: var(--text-muted);
    font-size: 0.7rem;
    flex-shrink: 0;
  }

  .btn-remove {
    background: transparent;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 0.8rem;
    padding: 0 0.25rem;
    line-height: 1;
    transition: color 0.2s;
  }

  .btn-remove:hover {
    color: var(--error-color);
  }

  .empty-state {
    font-size: 0.8rem;
    color: var(--text-muted);
    text-align: center;
    padding: 1rem 0;
  }

  .template-help {
    margin-top: 1rem;
    padding: 0.75rem;
    background: var(--hover-background);
    border-radius: 4px;
    border: 1px solid var(--border-color);
  }

  .help-text {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-bottom: 0.5rem;
  }

  .help-list {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 0.7rem;
    color: var(--text-muted);
  }

  .help-list li {
    padding: 0.15rem 0;
  }

  .help-list strong {
    color: var(--text-primary);
    font-family: 'SF Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
    font-size: 0.7rem;
  }
</style>
